import React, { useState, useEffect } from "react";
import { Claim, ClaimWithDetails, ClaimDocument, DocumentMetadata } from "@/types";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatCurrency, formatDate } from "@/lib/utils";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import ReactMarkdown from "react-markdown";
import { useReviewEdit } from "@/hooks/use-review-edit";
import { EditableJudgmentBadge } from "@/components/claims/editable-judgment-badge";
import { EditableAnalysis } from "@/components/claims/editable-analysis";
import { AddReviewItemModal } from "@/components/claims/add-review-item-modal";
import {
  FileText,
  FileCheck,
  Send,
  CheckCircle,
  FileQuestion,
  Calculator,
  ThumbsUp,
  ThumbsDown,
  BanIcon,
  BookText,
  ZapIcon,
  AlertTriangle,
  ClipboardList,
  RulerIcon,
  MessageSquare,
  Loader2,
  Download,
  ChevronLeft,
  ChevronRight,
  Plus,
} from "lucide-react";
import DocumentUploader from "@/components/documents/document-uploader";
import DocumentList from "@/components/documents/document-list";
import SimpleClaimDetails from "@/components/claims/simple-claim-details";
import ContractReviewDocumentViewer from "@/components/claims/contract-review-document-viewer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ReactPDFViewer from "@/components/documents/react-pdf-viewer";
import AsyncProgress from "@/components/ui/async-progress";
import { createClaimExtractionCheckpoints, createContractReviewCheckpoints, createQuantityReviewCheckpoints, createCalculationReviewCheckpoints } from "@/lib/progress-checkpoints";
import { useBackgroundJobStatus } from "@/hooks/use-background-job-status";

// Navigation context types (matching claim-detail-page.tsx)
type ViewMode = 'active' | 'reviewing';

// Review data interfaces
interface ReviewData {
  summary?: string;
  [key: string]: unknown;
}

interface ReviewItem {
  reviewData?: ReviewData | string;
  extractionNotes?: string;
  status?: string;
  [key: string]: unknown;
}

interface NavigationContext {
  viewMode: ViewMode;
  reviewingStage: string | null;
  activeStage: string;
}

interface ClaimStageWorkflowProps {
  claimData: ClaimWithDetails;
  navigationContext: NavigationContext;
  currentViewingStage: string | null;
  onOpenEmailDialog: () => void;
  onClaimUpdate: () => void;
  onReturnToActiveStage: () => void;
}

export default function ClaimStageWorkflow({ 
  claimData, 
  navigationContext, 
  currentViewingStage, 
  onOpenEmailDialog, 
  onClaimUpdate, 
  onReturnToActiveStage 
}: ClaimStageWorkflowProps) {
  const claim = claimData.claim;
  
  // Determine which stage to render content for
  const viewingStage = currentViewingStage || claim.status;
  const isReviewMode = navigationContext.viewMode === 'reviewing';
  
  // Render the appropriate workflow based on the viewing stage
  const renderWorkflow = () => {
    switch (viewingStage) {
      case "Acknowledgment":
        return <AcknowledgeClaimFlow 
          claim={claim} 
          isReviewMode={isReviewMode}
          onReturnToActiveStage={onReturnToActiveStage}
        />;
      case "Documents":
        return <UploadDocumentsFlow 
          claim={claim} 
          documents={claimData.documents} 
          isReviewMode={isReviewMode}
          onClaimUpdate={onClaimUpdate}
          onReturnToActiveStage={onReturnToActiveStage}
        />;
      case "Details":
        return <PopulateDetailsFlow 
          claim={claim} 
          isReviewMode={isReviewMode}
          onClaimUpdate={onClaimUpdate}
          onReturnToActiveStage={onReturnToActiveStage}
        />;
      case "Contract Review":
        return <ReviewContractFlow 
          claim={claim} 
          isReviewMode={isReviewMode}
          onClaimUpdate={onClaimUpdate}
          onReturnToActiveStage={onReturnToActiveStage}
        />;
      case "Quantity Review":
        return <ReviewQuantitiesFlow 
          claim={claim} 
          isReviewMode={isReviewMode}
          onClaimUpdate={onClaimUpdate}
          onReturnToActiveStage={onReturnToActiveStage}
        />;
      case "Calculation Review":
        return <ReviewCalculationsFlow 
          claim={claim} 
          isReviewMode={isReviewMode}
          onClaimUpdate={onClaimUpdate}
          onReturnToActiveStage={onReturnToActiveStage}
        />;
      case "Final Decision":
        return <FinalDecisionFlow 
          claim={claim} 
          isReviewMode={isReviewMode}
          onOpenEmailDialog={onOpenEmailDialog} 
          onClaimUpdate={onClaimUpdate}
          onReturnToActiveStage={onReturnToActiveStage}
        />;
      case "Approved":
      case "Rejected":
      case "Withdrawn":
        return <FinalStatusFlow claim={claim} />;
      default:
        return <div className="text-center py-8">No workflow available for this stage.</div>;
    }
  };
  
  return (
    <div className="space-y-6">
      {renderWorkflow()}
    </div>
  );
}

// Acknowledge Claim Flow (Email to contractor)
function AcknowledgeClaimFlow({ 
  claim, 
  isReviewMode: _isReviewMode = false, 
  onReturnToActiveStage: _onReturnToActiveStage 
}: { 
  claim: Claim, 
  isReviewMode?: boolean, 
  onReturnToActiveStage?: () => void 
}) {
  const { toast } = useToast();
  const [contractorEmail, setContractorEmail] = useState("");
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isSending, setIsSending] = useState(false);
  
  // Check if stage can be marked complete (email sent)
  const canMarkComplete = isEmailSent && contractorEmail.trim() !== "";
  
  const handleSendEmail = async () => {
    if (!contractorEmail.trim()) {
      toast({
        title: "Email required",
        description: "Please enter the contractor's email address.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSending(true);
    
    try {
      // Prepare email data
      const emailData = {
        to: contractorEmail.trim(),
        subject: `Claim Acknowledgment: ${claim.claimIdentifier || `#${claim.claimId.substring(0, 8)}`}`,
        body: `Dear Contractor,

We are writing to acknowledge receipt of your claim ${claim.claimIdentifier || `#${claim.claimId.substring(0, 8)}`} for the project, received on ${formatDate(claim.createdAt)}.

Your claim is currently under review, and we will provide a response within the contractually required timeframe.

If you have any questions or need to provide additional information, please contact us.

Regards,
Claims Review Team`
      };
      
      // Send email through API and log communication
      const logData = {
        claimId: claim.claimId,
        logType: "Email Sent",
        subject: emailData.subject,
        body: emailData.body,
        recipients: emailData.to
      };
      
      await apiRequest("POST", `/api/claims/${claim.claimId}/communicationLogs`, logData);
      
      setIsEmailSent(true);
      toast({
        title: "Email sent",
        description: "Acknowledgment email has been sent to the contractor and logged.",
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to send email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };
  
  const handleMarkComplete = async () => {
    try {
      // Update claim status to the next stage
      await apiRequest("PUT", `/api/claims/${claim.claimId}`, { 
        status: "Documents" 
      });
      
      queryClient.invalidateQueries({ queryKey: [`/api/claims/${claim.claimId}`] });
      
      toast({
        title: "Stage completed",
        description: "Moved to next stage: Upload Documents",
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to update claim status.",
        variant: "destructive",
      });
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Send className="h-5 w-5 mr-2 text-primary" />
          Acknowledge Claim Receipt
        </CardTitle>
        <CardDescription>
          Send an acknowledgment email to the contractor to confirm receipt of their claim.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <FileText className="h-5 w-5" />
          <AlertTitle>Action Required</AlertTitle>
          <AlertDescription>
            Before proceeding with the claim review, you need to acknowledge receipt to the contractor.
            This will send an email confirmation and update the claim status.
          </AlertDescription>
        </Alert>
        
        {/* Contractor Email Input */}
        <div className="space-y-2">
          <Label htmlFor="contractor-email">Contractor Email Address</Label>
          <Input
            id="contractor-email"
            type="email"
            placeholder="Enter contractor's email address"
            value={contractorEmail}
            onChange={(e) => setContractorEmail(e.target.value)}
            disabled={isEmailSent}
            className={isEmailSent ? "bg-muted" : ""}
          />
          {isEmailSent && (
            <div className="flex items-center text-sm text-green-600">
              <CheckCircle className="h-4 w-4 mr-1" />
              Email sent successfully
            </div>
          )}
        </div>
        
        <div className="border rounded-lg p-4 bg-muted/30">
          <h4 className="font-medium mb-2">Template Preview</h4>
          <div className="text-sm text-muted-foreground mb-2">
            <div><strong>Subject:</strong> Claim Acknowledgment: {claim.claimIdentifier || `#${claim.claimId.substring(0, 8)}`}</div>
            <div><strong>To:</strong> {contractorEmail || "[Contractor Email]"}</div>
          </div>
          <div className="border rounded p-3 bg-white text-sm">
            <p>Dear [Organization],</p>
            <br />
            <p>We are writing to acknowledge receipt of your claim {claim.claimIdentifier || `#${claim.claimId.substring(0, 8)}`} for project [Project Name], received on {formatDate(claim.createdAt)}.</p>
            <br />
            <p>Your claim is currently under review, and we will provide a response within the contractually required timeframe.</p>
            <br />
            <p>If you have any questions or need to provide additional information, please contact us.</p>
            <br />
            <p>Regards,<br />[Your Name]<br />[Your Organization]</p>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button 
          variant="outline"
          onClick={handleSendEmail}
          disabled={isSending || isEmailSent || !contractorEmail.trim()}
        >
          {isSending ? "Sending..." : isEmailSent ? "Email Sent" : "Send Acknowledgment Email"}
        </Button>
        
        <Button 
          onClick={handleMarkComplete}
          disabled={!canMarkComplete}
          className={canMarkComplete ? "bg-green-600 hover:bg-green-700" : ""}
        >
          {canMarkComplete ? "Mark Stage Complete" : "Complete Action First"}
        </Button>
      </CardFooter>
    </Card>
  );
}

// Upload Documents Flow
function UploadDocumentsFlow({ 
  claim, 
  documents, 
  isReviewMode = false,
  onClaimUpdate,
  onReturnToActiveStage 
}: { 
  claim: Claim, 
  documents: Array<{document: DocumentMetadata, claimDocument: ClaimDocument}>, 
  isReviewMode?: boolean,
  onClaimUpdate: () => void,
  onReturnToActiveStage?: () => void
}) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmitDocuments = async () => {
    setIsSubmitting(true);
    
    try {
      const response = await apiRequest("POST", `/api/claims/${claim.claimId}/confirm-documents`, {});
      
      if (response.ok) {
        const _result = await response.json();
        
        // Transition to Details status to skip separate processing stage
        await apiRequest("PUT", `/api/claims/${claim.claimId}`, { 
          status: "Details" 
        });
        
        toast({
          title: "Documents submitted",
          description: `${_result.documentsProcessed} document(s) have been submitted for AI processing`
        });
        
        // Invalidate queries to refresh claim data
        queryClient.invalidateQueries({ queryKey: [`/api/claims/${claim.claimId}`] });
        
        // Call parent update handler
        onClaimUpdate();
      } else {
        throw new Error('Failed to submit documents');
      }
    } catch {
      toast({
        title: "Submission failed",
        description: "Failed to submit documents for processing. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <FileCheck className="h-5 w-5 mr-2 text-primary" />
          {isReviewMode ? "Reviewing: Documents" : "Required Documents"}
        </CardTitle>
        <CardDescription>
          {isReviewMode 
            ? "Review uploaded documents. Adding new documents will re-trigger detail extraction." 
            : "Upload documents needed for claim processing. These will be used for data extraction and review."
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        
        {/* Review Mode Warning */}
        {isReviewMode && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-amber-800">Review Mode</h4>
                <p className="text-sm text-amber-700 mt-1">
                  You are reviewing documents from a completed stage. Adding new documents will re-trigger the detail extraction process and may affect downstream stages.
                </p>
                {onReturnToActiveStage && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-2 border-amber-300 text-amber-700 hover:bg-amber-100"
                    onClick={onReturnToActiveStage}
                  >
                    Return to Current Stage
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
        {/* Document List - Show only if there are documents */}
        {documents.length > 0 && (
          <DocumentList 
            documents={documents.map(d => ({
              ...d.document,
              documentType: d.claimDocument.documentType || "claim"
            }))} 
            entityType="claim" 
            entityId={claim.claimId}
            hideUploadButton={true} // Hide any upload button in DocumentList
          />
        )}
        
        {/* Central Document Uploader - Always visible */}
        <DocumentUploader 
          entityType="claim" 
          entityId={claim.claimId} 
          onSuccess={onClaimUpdate}
        />
        
        {/* Submit Section with proper spacing */}
        <div className="pt-6 border-t">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {documents.length} document{documents.length !== 1 ? 's' : ''} uploaded
            </div>
            <Button 
              disabled={documents.length === 0 || isSubmitting} 
              className="mt-2" // Add margin to avoid toast overlap
              onClick={handleSubmitDocuments}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Documents for Detail Extraction'
              )}
            </Button>
          </div>
          {documents.length === 0 && (
            <p className="text-sm text-muted-foreground mt-2">
              Upload at least 1 document to proceed
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Populate Details Flow
function PopulateDetailsFlow({ 
  claim, 
  isReviewMode: _isReviewMode = false, 
  onClaimUpdate, 
  onReturnToActiveStage: _onReturnToActiveStage 
}: { 
  claim: Claim, 
  isReviewMode?: boolean, 
  onClaimUpdate: () => void, 
  onReturnToActiveStage?: () => void 
}) {
  const { toast } = useToast();
  const [isExtracting, setIsExtracting] = useState(false);
  const [processingDocuments, setProcessingDocuments] = useState<Array<{fileName: string, documentType: string}>>([]);
  const [processingStartTime, setProcessingStartTime] = useState<Date | null>(null);
  const [_backgroundJobId, setBackgroundJobId] = useState<string | null>(null);

  // Use background job status hook to track real progress
  const { jobs: _jobs, activeJobs, overallStatus: _overallStatus } = useBackgroundJobStatus({
    entityId: claim.claimId,
    entityType: 'claim',
    onComplete: (job) => {
      console.log('Claim extraction job completed:', job);
      setIsExtracting(false);
      setProcessingDocuments([]);
      setProcessingStartTime(null);
      setBackgroundJobId(null);
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: [`/api/claims/${claim.claimId}`] });
      onClaimUpdate();
      
      toast({
        title: "Claim details extracted",
        description: "Claim details have been successfully extracted from your documents."
      });
    },
    onFailed: (job) => {
      console.log('Claim extraction job failed:', job);
      setIsExtracting(false);
      setProcessingDocuments([]);
      setProcessingStartTime(null);
      setBackgroundJobId(null);
      
      toast({
        title: "Claim extraction failed",
        description: job.error_message || "Processing failed. Please try again.",
        variant: "destructive"
      });
    }
  });
  
  // Check if claim has details populated (extracted)
  const hasClaimDetails = claim.claimAmount && claim.claimAmount > 0 && claim.claimHeadline;
  
  // Mutation to extract claim details
  const handleExtractDetails = async () => {
    setIsExtracting(true);
    setProcessingStartTime(new Date());
    
    try {
      // First, get the documents for processing display
      const documentsResponse = await apiRequest("GET", `/api/claims/${claim.claimId}/documents`);
      const documentsData = await documentsResponse.json();
      
      // Set processing documents for display
      setProcessingDocuments(documentsData.map((doc: {fileName: string, documentType?: string}) => ({
        fileName: doc.fileName,
        documentType: doc.documentType || 'Claim Document'
      })));
      
      const response = await apiRequest("POST", `/api/claims/${claim.claimId}/extract-details`);
      const _result = await response.json();
      
      // Store the background job ID for tracking
      if (_result.background_job_id) {
        setBackgroundJobId(_result.background_job_id);
      }
      
      toast({
        title: "Extraction started",
        description: "Claim details are being extracted from your documents. Progress will be tracked automatically."
      });
      
    } catch {
      setIsExtracting(false);
      setProcessingDocuments([]);
      setProcessingStartTime(null);
      
      toast({
        title: "Failed to start extraction",
        description: "Unable to start claim detail extraction. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  const handleConfirmDetails = async () => {
    try {
      // Update claim status to next stage
      await apiRequest("PUT", `/api/claims/${claim.claimId}`, { status: "Contract Review" });
      
      queryClient.invalidateQueries({ queryKey: [`/api/claims/${claim.claimId}`] });
      
      toast({
        title: "Details confirmed",
        description: "The claim details have been confirmed and the claim moved to Contract Review.",
      });
      
      onClaimUpdate();
    } catch {
      toast({
        title: "Error",
        description: "Failed to confirm claim details.",
        variant: "destructive",
      });
    }
  };
  
  // Show processing status if claim extraction is in progress or there are active background jobs
  const hasActiveJob = activeJobs.length > 0 || isExtracting;
  const activeJob = activeJobs.find(job => job.entity_type === 'claim') || null;
  
  if (hasActiveJob) {
    const checkpoints = createClaimExtractionCheckpoints(processingDocuments.length);
    
    // Get current checkpoint from background job if available
    const currentCheckpointId = (activeJob?.processing_data && typeof activeJob.processing_data === 'object' && 'current_checkpoint' in activeJob.processing_data && typeof activeJob.processing_data.current_checkpoint === 'string') ? activeJob.processing_data.current_checkpoint : 'initializing';
    const jobStartTime = activeJob?.started_at ? new Date(activeJob.started_at) : processingStartTime;
    
    // Map background job status to AsyncProgress status
    const progressStatus = activeJob ? activeJob.status as 'pending' | 'processing' | 'completed' | 'failed' | 'retry' : 'processing';
    
    return (
      <AsyncProgress
        status={progressStatus}
        workflowTitle="Extracting Claim Details"
        workflowDescription="AI agents are analyzing claim documents to extract amounts, descriptions, and supporting details."
        checkpoints={checkpoints}
        currentCheckpointId={currentCheckpointId}
        startTime={jobStartTime || undefined}
        documents={processingDocuments}
        onCancel={() => {
          setIsExtracting(false);
          setProcessingDocuments([]);
          setProcessingStartTime(null);
          toast({
            title: "Processing cancelled",
            description: "You can retry claim detail extraction from this stage.",
          });
        }}
        showAIIndicator={true}
      />
    );
  }
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <ClipboardList className="h-5 w-5 mr-2 text-primary" />
          Populate Claim Details
        </CardTitle>
        <CardDescription>
          {hasClaimDetails 
            ? "Review and confirm the extracted claim details from the uploaded documents."
            : "Extract claim details from your uploaded documents using AI analysis."
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {!hasClaimDetails ? (
          // Show extraction prompt when no details are populated
          <Alert>
            <ZapIcon className="h-4 w-4 text-primary" />
            <AlertTitle>Ready for AI Extraction</AlertTitle>
            <AlertDescription>
              Click the button below to analyze your uploaded documents and automatically extract 
              claim details including amounts, descriptions, and key information.
            </AlertDescription>
          </Alert>
        ) : (
          // Show confirmation when details are already populated
          <Alert>
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertTitle>Details Extracted</AlertTitle>
            <AlertDescription>
              Our system has analyzed your documents and extracted the key claim details. 
              Please review and adjust as needed before confirming.
            </AlertDescription>
          </Alert>
        )}
        
        {/* Show claim details if they exist */}
        {hasClaimDetails && (
          <SimpleClaimDetails 
            claimIdentifier={claim.claimIdentifier}
            claimAmount={claim.claimAmount || 0}
            claimAmountCurrency={claim.claimAmountCurrency}
            claimType={claim.claimType}
            claimHeadline={claim.claimHeadline}
            claimSummary={claim.claimSummary}
          />
        )}
      </CardContent>
      <CardFooter className="justify-end">
        {!hasClaimDetails ? (
          // Show extraction button when no details
          <>
            <Button variant="outline" className="mr-2">
              Request Additional Information
            </Button>
            <Button 
              onClick={handleExtractDetails}
              disabled={isExtracting}
            >
              {isExtracting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Extracting...
                </>
              ) : (
                'Extract Details'
              )}
            </Button>
          </>
        ) : (
          // Show confirmation button when details exist
          <>
            <Button variant="outline" className="mr-2" onClick={handleExtractDetails}>
              Re-extract Details
            </Button>
            <Button 
              onClick={handleConfirmDetails}
            >
              Confirm Claim Details
            </Button>
          </>
        )}
      </CardFooter>
    </Card>
  );
}

// Contract Review Flow
function ReviewContractFlow({ 
  claim, 
  isReviewMode: _isReviewMode = false, 
  onClaimUpdate, 
  onReturnToActiveStage: _onReturnToActiveStage 
}: { 
  claim: Claim, 
  isReviewMode?: boolean, 
  onClaimUpdate: () => void, 
  onReturnToActiveStage?: () => void 
}) {
  const { toast } = useToast();
  
  // Background job tracking for contract review
  const { jobs: _jobs, activeJobs, overallStatus: _overallStatus } = useBackgroundJobStatus({
    entityId: claim.claimId,
    entityType: 'claim',
    onComplete: (job) => {
      console.log('Contract review job completed:', job);
      if (job.job_type === 'contract_review') {
        setReviewState(prev => ({ ...prev, status: 'completed' }));
        fetchExistingReview(); // Refresh review data
        onClaimUpdate();
        
        toast({
          title: "Contract review completed",
          description: "The contract review has been completed successfully."
        });
      }
    },
    onFailed: (job) => {
      console.log('Contract review job failed:', job);
      if (job.job_type === 'contract_review') {
        setReviewState(prev => ({ ...prev, status: 'failed' }));
        
        toast({
          title: "Contract review failed",
          description: job.error_message || "Review processing failed. Please try again.",
          variant: "destructive"
        });
      }
    }
  });
  
  // Add proper type interfaces
  interface DocumentResponse {
    documentMetadataId: string;
    fileName: string;
    documentType?: string;
    fileSize?: number;
    uploadDate?: string;
    createdAt?: string;
    currentVersionId?: string;
    filePath?: string;
  }
  
  interface ContractReviewResult {
    status: 'not_started' | 'processing' | 'completed' | 'failed';
    completedAt?: string;
    reviewData?: {
      summary?: string;
      review_items?: Array<{
        item_id: string;
        title: string;
        judgment: 'valid' | 'invalid' | 'unclear';
        explanation: string;
        claim_passage?: {
          text: string;
          page_number?: number;
          document_metadata_id?: string;
        };
        contract_passages?: Array<{
          text: string;
          page_number?: number;
          document_metadata_id?: string;
        }>;
        // Audit trail fields
        original_judgment?: string;
        original_explanation?: string;
        is_ai_generated?: boolean;
        is_modified?: boolean;
        created_by?: string;
        last_modified_by?: string;
        last_modified_at?: string;
        is_deleted?: boolean;
        edit_history?: Array<{
          editId: string;
          action: 'created' | 'modified_judgment' | 'modified_explanation' | 'deleted' | 'restored';
          userId: string;
          userName: string;
          timestamp: string;
          previousValues?: { judgment?: string; explanation?: string; };
          newValues?: { judgment?: string; explanation?: string; };
        }>;
      }>;
      claim_documents?: Array<{
        document_id: string;
        document_name: string;
        download_url: string;
      }>;
      contract_documents?: Array<{
        document_id: string;
        document_name: string;
        download_url: string;
      }>;
    };
    extractionNotes?: string;
  }
  
  // Removed unused ContractReviewStatus interface
  
  const [reviewState, setReviewState] = useState({
    status: 'not_started' as 'not_started' | 'processing' | 'completed' | 'failed',
    lastReviewDate: null as Date | null,
    processingStartTime: null as Date | null,
    expandedItemId: null as string | null,
    selectedPassageIndex: 0,
    activeClaimDocumentId: null as string | null,
    activeContractDocumentId: null as string | null,
    targetHighlightId: null as string | null,
  });
  const [reviewData, setReviewData] = useState<ContractReviewResult | null>(null);
  const [contractDocuments, setContractDocuments] = useState<DocumentResponse[]>([]);
  const [claimDocuments, setClaimDocuments] = useState<DocumentResponse[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);

  const fetchExistingReview = async () => {
    try {
      const response = await apiRequest("GET", `/api/claims/${claim.claimId}/contract-review`);
      const reviewResult: ContractReviewResult = await response.json();
      if (reviewResult) {
        setReviewData(reviewResult);
        setReviewState(prev => ({ 
          ...prev, 
          status: reviewResult.status,
          lastReviewDate: reviewResult.completedAt ? new Date(reviewResult.completedAt) : null,
        }));
        
        // If processing, start polling
        // TODO: Implement startPolling function or use hook refresh
        // if (reviewResult.status === 'processing') {
        //   // startPolling(); // TODO: Implement
        // }
      }
    } catch {
      // No existing review - keep status as not_started
      console.log('No existing review found');
    }
  };

  // Review editing functionality
  const { editItem, createItem, deleteItem, isEditing, isDeleting } = useReviewEdit({
    onDataRefresh: fetchExistingReview
  });
  
  // Modal state for adding custom review items
  const [showContractAddItemModal, setShowContractAddItemModal] = useState(false);

  // Fetch contract documents and existing review on component mount
  React.useEffect(() => {
    fetchContractDocuments();
    fetchExistingReview();
    
    // Cleanup handled by useBackgroundJobStatus hook
  }, []);

  const fetchContractDocuments = async () => {
    try {
      setDocumentsLoading(true);
      console.log('fetchContractDocuments: Starting document fetch');
      
      // Get claim documents - FIX: Parse JSON response
      const claimDocsResponse = await apiRequest("GET", `/api/claims/${claim.claimId}/documents`);
      const claimDocsData: DocumentResponse[] = await claimDocsResponse.json();
      console.log('fetchContractDocuments: Claim docs response:', claimDocsData);
      setClaimDocuments(claimDocsData || []);

      // Get project contract documents - we need to get the project first - FIX: Parse JSON response
      const claimResponse = await apiRequest("GET", `/api/claims/${claim.claimId}`);
      const claimData: { claim: { projectId: string } } = await claimResponse.json();
      console.log('fetchContractDocuments: Claim response:', claimData);
      
      if (claimData?.claim?.projectId) {
        console.log('fetchContractDocuments: Fetching project documents for project:', claimData.claim.projectId);
        const allProjectDocsResponse = await apiRequest("GET", `/api/projects/${claimData.claim.projectId}/documents`);
        const allProjectDocsData: DocumentResponse[] = await allProjectDocsResponse.json();
        console.log('fetchContractDocuments: All project docs response:', allProjectDocsData);
        
        // Filter for contract documents (case-insensitive)
        const contractDocs = (allProjectDocsData || []).filter((doc: DocumentResponse) => 
          doc.documentType?.toLowerCase() === 'contract'
        );
        console.log('fetchContractDocuments: Filtered contract docs:', contractDocs);
        setContractDocuments(contractDocs);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setDocumentsLoading(false);
    }
  };


  // const handleDownload = async (documentUrl: string, documentName: string) => {
  //   try {
  //     // First fetch the download URL from the API with proper authentication
  //     const response = await apiRequest("GET", documentUrl);
  //     const data = await response.json();
  //     
  //     // Open the signed URL in a new tab
  //     window.open(data.downloadUrl, '_blank');
  //     
  //     toast({
  //       title: "Download started",
  //       description: `Downloading ${documentName}`,
  //     });
  //   } catch (error) {
  //     console.error('Download error:', error);
  //     toast({
  //       title: "Download failed",
  //       description: error instanceof Error ? error.message : "Failed to download document",
  //       variant: "destructive",
  //     });
  //   }
  // };

  const handleTriggerReview = async () => {
    if (contractDocuments.length === 0) {
      toast({
        title: "No Contract Documents",
        description: "Contract documents must be uploaded to the project before conducting a review.",
        variant: "destructive",
      });
      return;
    }

    if (claimDocuments.length === 0) {
      toast({
        title: "No Claim Documents",
        description: "Claim documents must be uploaded before conducting a review.",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await apiRequest("POST", `/api/claims/${claim.claimId}/contract-review`);
      const _result = await response.json();
      
      // Only set processing state after successful API call
      setReviewState(prev => ({ ...prev, status: 'processing', processingStartTime: new Date() }));
      
      toast({
        title: "Review Started",
        description: "The automated contract review has been started. Progress will be tracked automatically.",
      });
      
      // Background job tracking will handle completion automatically
    } catch (error: unknown) {
      // Check for 409 conflict error specifically
      if (error instanceof Error && (error.message.includes('409') || error.message.includes('already in progress'))) {
        toast({
          title: "Review Already Running",
          description: "A contract review is already in progress. Please wait for it to complete before starting a new one.",
          variant: "destructive",
        });
      } else {
        const errorMessage = error instanceof Error ? error.message : "Failed to start contract review.";
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      }
    }
  };

  const handleItemExpansion = (itemId: string) => {
    const wasExpanded = reviewState.expandedItemId === itemId;
    const newExpandedId = wasExpanded ? null : itemId;
    
    setReviewState(prev => ({
      ...prev,
      expandedItemId: newExpandedId,
      selectedPassageIndex: 0, // Reset to first passage when expanding new item
      targetHighlightId: newExpandedId, // Set target highlight for navigation
    }));

    // Auto-open appropriate documents when expanding review item
    if (newExpandedId && reviewData?.reviewData?.review_items) {
      const expandedItem = reviewData.reviewData.review_items.find(item => item.item_id === itemId);
      if (expandedItem) {
        console.log('📖 Expanding review item:', {
          itemId,
          title: expandedItem.title,
          claimPassage: expandedItem.claim_passage,
          contractPassages: expandedItem.contract_passages?.length,
          claimDocId: expandedItem.claim_passage?.document_metadata_id,
          contractDocIds: expandedItem.contract_passages?.map(p => p.document_metadata_id),
          targetHighlight: newExpandedId,
        });

        // Open claim document for claim passage
        if (expandedItem.claim_passage?.document_metadata_id) {
          const claimDocId = expandedItem.claim_passage.document_metadata_id;
          console.log('📄 Auto-opening claim document:', {
            documentId: claimDocId,
            pageNumber: expandedItem.claim_passage.page_number,
            targetHighlight: newExpandedId,
          });
          setReviewState(prev => ({
            ...prev,
            activeClaimDocumentId: claimDocId,
          }));
        }
        
        // Open contract document for currently selected contract passage (index 0 by default)
        const currentPassageIndex = 0; // Since we reset to 0 above
        if (expandedItem.contract_passages && expandedItem.contract_passages.length > currentPassageIndex) {
          const selectedPassage = expandedItem.contract_passages[currentPassageIndex];
          const contractDocId = selectedPassage.document_metadata_id || null;
          console.log('📋 Auto-opening contract document:', {
            documentId: contractDocId,
            passageIndex: currentPassageIndex,
            pageNumber: selectedPassage.page_number,
            passageData: selectedPassage,
            targetHighlight: newExpandedId,
          });
          setReviewState(prev => ({
            ...prev,
            activeContractDocumentId: contractDocId,
          }));
        }
      }
    }
  };

  const handlePassageNavigation = (direction: 'prev' | 'next', maxPassages: number) => {
    const newIndex = direction === 'next' 
      ? Math.min(reviewState.selectedPassageIndex + 1, maxPassages - 1)
      : Math.max(reviewState.selectedPassageIndex - 1, 0);
    
    setReviewState(prev => ({
      ...prev,
      selectedPassageIndex: newIndex,
    }));

    // Update active contract document when passage changes
    if (reviewState.expandedItemId && reviewData?.reviewData?.review_items) {
      const expandedItem = reviewData.reviewData.review_items.find(item => item.item_id === reviewState.expandedItemId);
      if (expandedItem?.contract_passages && expandedItem.contract_passages.length > newIndex) {
        const selectedPassage = expandedItem.contract_passages[newIndex];
        const contractDocId = selectedPassage.document_metadata_id || null;
        console.log('📋 Switching to contract document:', {
          documentId: contractDocId,
          passageIndex: newIndex,
          pageNumber: selectedPassage.page_number,
          passageData: selectedPassage,
        });
        setReviewState(prev => ({
          ...prev,
          activeContractDocumentId: contractDocId,
        }));
      }
    }
  };

  const handleDocumentSelect = (documentId: string, documentType: 'claim' | 'contract') => {
    if (documentType === 'claim') {
      setReviewState(prev => ({ ...prev, activeClaimDocumentId: documentId }));
    } else {
      setReviewState(prev => ({ ...prev, activeContractDocumentId: documentId }));
    }
  };

  // const getJudgmentIcon = (judgment: 'valid' | 'invalid' | 'unclear') => {
  //   switch (judgment) {
  //     case 'valid':
  //       return <CheckCircle className="h-4 w-4 text-green-600" />;
  //     case 'invalid':
  //       return <AlertTriangle className="h-4 w-4 text-red-600" />;
  //     case 'unclear':
  //       return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
  //     default:
  //       return null;
  //   }
  // };

  // const getJudgmentColor = (judgment: 'valid' | 'invalid' | 'unclear') => {
  //   switch (judgment) {
  //     case 'valid':
  //       return 'bg-green-100 text-green-800 border-green-200';
  //     case 'invalid':
  //       return 'bg-red-100 text-red-800 border-red-200';
  //     case 'unclear':
  //       return 'bg-yellow-100 text-yellow-800 border-yellow-200';
  //     default:
  //       return '';
  //   }
  // };

  // const getJudgmentRing = (judgment: 'valid' | 'invalid' | 'unclear') => {
  //   switch (judgment) {
  //     case 'valid':
  //       return 'ring-2 ring-green-300/70';
  //     case 'invalid':
  //       return 'ring-2 ring-red-300/70';
  //     case 'unclear':
  //       return 'ring-2 ring-yellow-300/70';
  //     default:
  //       return '';
  //   }
  // };

  const getJudgmentIcon = (judgment: 'valid' | 'invalid' | 'unclear') => {
    switch (judgment) {
      case 'valid':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'invalid':
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      case 'unclear':
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      default:
        return null;
    }
  };

  const getJudgmentColor = (judgment: 'valid' | 'invalid' | 'unclear') => {
    switch (judgment) {
      case 'valid':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'invalid':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'unclear':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return '';
    }
  };

  const getJudgmentRing = (judgment: 'valid' | 'invalid' | 'unclear') => {
    switch (judgment) {
      case 'valid':
        return 'ring-2 ring-green-300/70';
      case 'invalid':
        return 'ring-2 ring-red-300/70';
      case 'unclear':
        return 'ring-2 ring-yellow-300/70';
      default:
        return '';
    }
  };

  const handleAdvanceStage = async () => {
    try {
      await apiRequest("PUT", `/api/claims/${claim.claimId}`, { 
        status: "Quantity Review" 
      });
      
      queryClient.invalidateQueries({ queryKey: [`/api/claims/${claim.claimId}`] });
      
      toast({
        title: "Stage completed",
        description: "Moved to next stage: Quantity Review",
      });
      
      onClaimUpdate();
    } catch {
      toast({
        title: "Error",
        description: "Failed to advance to next stage.",
        variant: "destructive",
      });
    }
  };


  interface TextHighlight {
    text: string;
    pageNumber: number;
    reviewItemId?: string;
    reviewItemIndex?: number;
    judgment?: 'valid' | 'invalid' | 'unclear';
    reviewItemTitle?: string;
  }

  interface ReviewItem {
    item_id: string;
    title: string;
    judgment: 'valid' | 'invalid' | 'unclear';
    claim_passage?: {
      text: string;
      document_metadata_id?: string;
      page_number?: number;
    };
    contract_passages?: Array<{
      text: string;
      document_metadata_id?: string;
      page_number?: number;
    }>;
  }

  interface ReviewData {
    reviewData?: {
      review_items?: ReviewItem[];
    };
  }

  // Helper function to create text-based highlights from review data
  // FOCUSED VERSION: Only return highlights for active review item and selected contract reference
  const createTextHighlights = (
    reviewData: ReviewData | undefined,
    activeReviewItemId?: string | null,
    activeContractPassageIndex?: number
  ): TextHighlight[] => {
    const textHighlights: TextHighlight[] = [];
    
    if (!reviewData?.reviewData?.review_items || !activeReviewItemId) {
      return textHighlights; // No highlights if no active review item
    }

    // Find the currently active review item
    const activeItem = reviewData.reviewData.review_items.find((item: ReviewItem) => 
      item.item_id === activeReviewItemId
    );

    if (!activeItem) {
      return textHighlights; // No highlights if active item not found
    }

    const itemIndex = reviewData.reviewData.review_items.findIndex((item: ReviewItem) => 
      item.item_id === activeReviewItemId
    );

    // Add claim passage text (always show for active item)
    if (activeItem.claim_passage?.text) {
      textHighlights.push({
        text: activeItem.claim_passage.text,
        pageNumber: activeItem.claim_passage.page_number || 1,
        reviewItemId: activeItem.item_id,
        reviewItemIndex: itemIndex + 1,
        judgment: activeItem.judgment,
        reviewItemTitle: activeItem.title,
      });
    }

    // Add ONLY the currently selected contract passage text
    if (activeItem.contract_passages && 
        typeof activeContractPassageIndex === 'number' && 
        activeContractPassageIndex >= 0 && 
        activeContractPassageIndex < activeItem.contract_passages.length) {
      
      const selectedPassage = activeItem.contract_passages[activeContractPassageIndex];
      if (selectedPassage.text) {
        textHighlights.push({
          text: selectedPassage.text,
          pageNumber: selectedPassage.page_number || 1,
          reviewItemId: activeItem.item_id,
          reviewItemIndex: itemIndex + 1,
          judgment: activeItem.judgment,
          reviewItemTitle: activeItem.title,
        });
      }
    }

    return textHighlights;
  };

  const createDocumentDataWithHighlights = (documents: DocumentResponse[]) => {
    console.log('🔧 Creating document data with highlights:', { documents });

    return documents.map(doc => {
      let targetPage = 1;

      // Create text highlights for the current document using text-based system
      const textHighlights = createTextHighlights(reviewData as ReviewData | undefined, reviewState.expandedItemId, reviewState.selectedPassageIndex);
      
      // Filter text highlights for this specific document
      const documentTextHighlights = textHighlights.filter(h => 
        reviewData?.reviewData?.review_items?.some((item: ReviewItem) => 
          (item.claim_passage?.document_metadata_id === doc.documentMetadataId && item.claim_passage?.text === h.text) ||
          (item.contract_passages?.some((p) => p.document_metadata_id === doc.documentMetadataId && p.text === h.text))
        )
      );

      // Set target page based on first highlight found
      if (documentTextHighlights.length > 0) {
        targetPage = documentTextHighlights[0].pageNumber;
      }

      console.log(`📊 Document ${doc.fileName} summary:`, {
        totalTextHighlights: documentTextHighlights.length,
        targetPage,
        textHighlights: documentTextHighlights.map(h => ({
          text: h.text.substring(0, 50) + '...',
          page: h.pageNumber,
          reviewItem: h.reviewItemIndex,
          judgment: h.judgment
        }))
      });

      return {
        documentId: doc.documentMetadataId,
        documentName: doc.fileName,
        documentUrl: `/api/documents/${doc.documentMetadataId}/download`,
        textHighlights: documentTextHighlights,
        targetPage
      };
    });
  };

  // const reviewItems: unknown[] = []; // TODO: Implement proper review items fetching
  const claimDocumentsWithHighlights = createDocumentDataWithHighlights(claimDocuments);
  const contractDocumentsWithHighlights = createDocumentDataWithHighlights(contractDocuments);

  // Check for active contract review jobs
  const activeContractReviewJob = activeJobs.find(job => job.job_type === 'contract_review') || null;
  const hasActiveContractReviewJob = activeContractReviewJob || reviewState.status === 'processing';
  
  // If contract review is processing, show AsyncProgress
  if (hasActiveContractReviewJob) {
    const checkpoints = createContractReviewCheckpoints(claimDocuments.length, contractDocuments.length);
    
    // Get current checkpoint from background job if available
    const currentCheckpointId = (activeContractReviewJob?.processing_data && typeof activeContractReviewJob.processing_data === 'object' && 'current_checkpoint' in activeContractReviewJob.processing_data && typeof activeContractReviewJob.processing_data.current_checkpoint === 'string') ? activeContractReviewJob.processing_data.current_checkpoint : 'initializing';
    const jobStartTime = activeContractReviewJob?.started_at ? new Date(activeContractReviewJob.started_at) : reviewState.processingStartTime;
    
    // Map background job status to AsyncProgress status
    const progressStatus = activeContractReviewJob ? activeContractReviewJob.status as 'pending' | 'processing' | 'completed' | 'failed' | 'retry' : 'processing';
    
    // Create documents array for display
    const allDocuments = [
      ...claimDocuments.map(doc => ({ fileName: doc.fileName, documentType: 'Claim Document' })),
      ...contractDocuments.map(doc => ({ fileName: doc.fileName, documentType: 'Contract' }))
    ];
    
    return (
      <div className="space-y-6">
        <AsyncProgress
          status={progressStatus}
          workflowTitle="Contract Review Analysis"
          workflowDescription="AI agents are comparing claim documents against contract terms to identify validity, conflicts, and coverage issues."
          checkpoints={checkpoints}
          currentCheckpointId={currentCheckpointId}
          startTime={jobStartTime || undefined}
          documents={allDocuments}
          onCancel={() => {
            setReviewState(prev => ({ ...prev, status: 'not_started', processingStartTime: null }));
            toast({
              title: "Review cancelled",
              description: "You can restart the contract review from this stage.",
            });
          }}
          showAIIndicator={true}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <BookText className="h-5 w-5 mr-2 text-primary" />
            Contract Review
          </CardTitle>
          <CardDescription>
            Analyze claim validity against project contract terms using AI-powered document review.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Review Trigger Section */}
          <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
            <div className="space-y-1">
              <h4 className="font-medium">Automated Contract Review</h4>
              <p className="text-sm text-muted-foreground">
                {reviewState.status === 'not_started' && "Ready to analyze claim documents against contract terms"}
                {reviewState.status === 'completed' && `Review completed ${reviewState.lastReviewDate ? formatDate(reviewState.lastReviewDate.toISOString()) : ''}`}
                {reviewState.status === 'failed' && "Review failed - please try again"}
              </p>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span>📄 Claim docs: {claimDocuments.length}</span>
                <span>📋 Contract docs: {contractDocuments.length}</span>
              </div>
            </div>
            <div className="flex gap-2">
              {(reviewState.status === 'not_started' || reviewState.status === 'failed') && (
                <Button 
                  onClick={handleTriggerReview}
                  disabled={contractDocuments.length === 0 || claimDocuments.length === 0}
                  className="min-w-[160px]"
                >
                  <ZapIcon className="h-4 w-4 mr-2" />
                  Conduct Automated Review
                </Button>
              )}
              {reviewState.status === 'completed' && (
                <Button variant="outline" onClick={handleTriggerReview} className="min-w-[160px]">
                  <ZapIcon className="h-4 w-4 mr-2" />
                  Re-Run Automated Review
                </Button>
              )}
            </div>
          </div>

          {/* Loading indicator */}
          {documentsLoading && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span className="ml-2 text-sm text-muted-foreground">Loading documents...</span>
            </div>
          )}

          {/* Warning for missing documents - only show when not loading */}
          {!documentsLoading && (contractDocuments.length === 0 || claimDocuments.length === 0) && (
            <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Missing Documents</AlertTitle>
            <AlertDescription>
                {contractDocuments.length === 0 && "No contract documents found for this project. "}
                {claimDocuments.length === 0 && "No claim documents uploaded. "}
                Please ensure all required documents are uploaded before conducting the review.
            </AlertDescription>
          </Alert>
          )}

          {/* Review Results Display */}
          {reviewState.status === 'completed' && reviewData?.reviewData && (
            <div className="space-y-6">
              {/* Summary Section */}
              <div className="prose prose-sm max-w-none">
                <div className="text-sm bg-slate-50 p-4 rounded-lg border">
                  <ReactMarkdown>{reviewData?.reviewData?.summary || 'No summary available'}</ReactMarkdown>
                </div>
              </div>

              {/* Review Items List */}
              {reviewData?.reviewData?.review_items && reviewData.reviewData.review_items.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Review Items ({reviewData.reviewData.review_items.length})</h4>
                    <Button
                      size="sm"
                      onClick={() => setShowContractAddItemModal(true)}
                      className="gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Add Item
                    </Button>
                  </div>
                  {reviewData.reviewData.review_items.map((item, index: number) => (
                    <div 
                      key={item.item_id || index}
                      className={`border rounded-lg transition-all duration-200 ${getJudgmentColor(item.judgment)} ${
                        reviewState.expandedItemId === item.item_id ? getJudgmentRing(item.judgment) : ''
                      }`}
                    >
                      <div 
                        className="p-4 cursor-pointer hover:bg-black/5 transition-colors"
                        onClick={() => handleItemExpansion(item.item_id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="font-mono text-sm text-muted-foreground">
                              {String(index + 1).padStart(2, '0')}
                            </span>
                            {getJudgmentIcon(item.judgment)}
                            <span className="font-medium">{item.title}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <EditableJudgmentBadge
                              value={item.judgment}
                              reviewType="contract"
                              claimId={claim.claimId}
                              itemId={item.item_id}
                              isAiGenerated={item.is_ai_generated}
                              isModified={item.is_modified}
                              lastModifiedBy={item.last_modified_by}
                              isEditing={isEditing(item.item_id)}
                              onEdit={(judgment) => editItem({
                                claimId: claim.claimId,
                                reviewType: 'contract',
                                itemId: item.item_id,
                                judgment
                              })}
                            />
                            {item.is_ai_generated === false && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (confirm('Are you sure you want to delete this review item?')) {
                                    deleteItem({
                                      claimId: claim.claimId,
                                      reviewType: 'contract',
                                      itemId: item.item_id,
                                    });
                                  }
                                }}
                                disabled={isDeleting}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Delete review item"
                              >
                                <BanIcon className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {reviewState.expandedItemId === item.item_id && (
                        <div className="border-t bg-white/50 p-4 space-y-4">
                          <div>
                            <p className="text-muted-foreground mb-2 text-sm">Analysis:</p>
                            <EditableAnalysis
                              value={item.explanation}
                              originalValue={item.original_explanation}
                              reviewType="contract"
                              claimId={claim.claimId}
                              itemId={item.item_id}
                              isAiGenerated={item.is_ai_generated}
                              isModified={item.is_modified}
                              lastModifiedBy={item.last_modified_by}
                              lastModifiedAt={item.last_modified_at}
                              editHistory={item.edit_history}
                              isEditing={isEditing(item.item_id)}
                              onEdit={(explanation) => editItem({
                                claimId: claim.claimId,
                                reviewType: 'contract',
                                itemId: item.item_id,
                                explanation
                              })}
                            />
                          </div>
                          
                          {/* Claim Passage */}
                          <div className="bg-blue-50 p-3 rounded border border-blue-200">
                            <p className="text-xs font-medium text-blue-700 mb-1">
                              Claim Document - Page {item.claim_passage?.page_number || 1}
                            </p>
                            <p className="text-sm">{item.claim_passage?.text}</p>
                          </div>
                          
                          {/* Contract Passages */}
                          {item.contract_passages && item.contract_passages.length > 0 && (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <p className="text-xs font-medium text-green-700">
                                  Contract References ({item.contract_passages.length})
                                </p>
                                {item.contract_passages.length > 1 && (
                                  <div className="flex items-center gap-2 text-xs">
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => handlePassageNavigation('prev', item.contract_passages?.length || 0)}
                                      disabled={reviewState.selectedPassageIndex === 0}
                                    >
                                      ←
                                    </Button>
                                    <span className="text-muted-foreground">
                                      {reviewState.selectedPassageIndex + 1} of {item.contract_passages?.length || 0}
                                    </span>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => handlePassageNavigation('next', item.contract_passages?.length || 0)}
                                      disabled={reviewState.selectedPassageIndex === (item.contract_passages?.length || 1) - 1}
                                    >
                                      →
                                    </Button>
                                  </div>
                                )}
                              </div>
                              <div className="bg-green-50 p-3 rounded border border-green-200">
                                <p className="text-xs font-medium text-green-700 mb-1">
                                  Contract Document - Page {item.contract_passages[reviewState.selectedPassageIndex]?.page_number || 1}
                                </p>
                                <p className="text-sm">{item.contract_passages[reviewState.selectedPassageIndex]?.text}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Processing Notes */}
              {reviewData.extractionNotes && (
                <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded">
                  <strong>Processing Notes:</strong> {reviewData.extractionNotes}
                </div>
              )}

              {/* Document Preview Columns */}
              {(claimDocumentsWithHighlights.length > 0 || contractDocumentsWithHighlights.length > 0) && (
                <div className="mt-6">
                  <h4 className="font-medium mb-4">Document Review</h4>
                  <ContractReviewDocumentViewer
                    claimDocuments={claimDocumentsWithHighlights}
                    contractDocuments={contractDocumentsWithHighlights}
                    activeClaimDocumentId={reviewState.activeClaimDocumentId || undefined}
                    activeContractDocumentId={reviewState.activeContractDocumentId || undefined}
                    autoScrollToHighlight={!!reviewState.expandedItemId}
                    targetHighlightId={reviewState.targetHighlightId || undefined}
                    onDocumentSelect={handleDocumentSelect}
                  />
                </div>
              )}
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button variant="outline" onClick={onClaimUpdate}>
            Refresh Review Status
          </Button>
          {reviewState.status === 'completed' && (
            <Button onClick={handleAdvanceStage}>
              Proceed to Quantity Review
              <CheckCircle className="h-4 w-4 ml-2" />
            </Button>
          )}
        </CardFooter>
      </Card>

      {/* Add Review Item Modal */}
      <AddReviewItemModal
        isOpen={showContractAddItemModal}
        onOpenChange={setShowContractAddItemModal}
        reviewType="contract"
        claimId={claim.claimId}
        onSubmit={(data) => {
          createItem({
            claimId: claim.claimId,
            reviewType: 'contract',
            title: data.title,
            judgment: data.judgment,
            explanation: data.explanation,
            claim_passage: data.claim_passage,
            contract_passages: data.contract_passages,
          });
          setShowContractAddItemModal(false);
        }}
      />
    </div>
  );
}

// Quantity Review Flow
function ReviewQuantitiesFlow({ 
  claim, 
  isReviewMode: _isReviewMode = false, 
  onClaimUpdate, 
  onReturnToActiveStage: _onReturnToActiveStage 
}: { 
  claim: Claim, 
  isReviewMode?: boolean, 
  onClaimUpdate: () => void, 
  onReturnToActiveStage?: () => void 
}) {
  const { toast } = useToast();
  
  // Review editing functionality
  const { editItem, editExplanation, createItem, deleteItem, isEditing, isCreating, isDeleting } = useReviewEdit();
  
  // Modal state for adding custom review items
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showContractAddItemModal, setShowContractAddItemModal] = useState(false);
  
  // Background job tracking for quantity review
  const { jobs: _jobs, activeJobs, overallStatus: _overallStatus } = useBackgroundJobStatus({
    entityId: claim.claimId,
    entityType: 'claim',
    onComplete: (job) => {
      console.log('Quantity review job completed:', job);
      if (job.job_type === 'quantity_review') {
        setReviewState(prev => ({ ...prev, status: 'completed' }));
        fetchExistingReview(); // Refresh review data
        onClaimUpdate();
        
        toast({
          title: "Quantity review completed",
          description: "The quantity review has been completed successfully."
        });
      }
    },
    onFailed: (job) => {
      console.log('Quantity review job failed:', job);
      if (job.job_type === 'quantity_review') {
        setReviewState(prev => ({ ...prev, status: 'failed' }));
        
        toast({
          title: "Quantity review failed",
          description: job.error_message || "Review processing failed. Please try again.",
          variant: "destructive"
        });
      }
    }
  });
  
  interface DocumentResponse {
    documentMetadataId: string;
    fileName: string;
    documentType?: string;
    fileSize?: number;
    uploadDate?: string;
    createdAt?: string;
    currentVersionId?: string;
    filePath?: string;
  }
  
  interface QuantityReviewResult {
    status: 'not_started' | 'processing' | 'completed' | 'failed';
    completedAt?: string;
    reviewData?: {
      summary?: string;
      review_items?: Array<{
        item_id: string;
        title: string;
        judgment: 'valid' | 'excessive' | 'unclear';
        explanation: string;
        claim_passage?: {
          text: string;
          page_number?: number;
          document_id?: string;
        };
        contract_passages?: Array<{
          text: string;
          page_number?: number;
          document_metadata_id?: string;
        }>;
        // Audit trail fields
        original_judgment?: string;
        original_explanation?: string;
        is_ai_generated?: boolean;
        is_modified?: boolean;
        created_by?: string;
        last_modified_by?: string;
        last_modified_at?: string;
        is_deleted?: boolean;
        edit_history?: Array<{
          editId: string;
          action: 'created' | 'modified_judgment' | 'modified_explanation' | 'deleted' | 'restored';
          userId: string;
          userName: string;
          timestamp: string;
          previousValues?: { judgment?: string; explanation?: string; };
          newValues?: { judgment?: string; explanation?: string; };
        }>;
      }>;
    };
    extractionNotes?: string;
  }
  
  // Removed unused QuantityReviewStatus interface
  
  const [reviewState, setReviewState] = useState({
    status: 'not_started' as 'not_started' | 'processing' | 'completed' | 'failed',
    lastReviewDate: null as Date | null,
    processingStartTime: null as Date | null,
    expandedItemId: null as string | null,
    activeDocumentId: null as string | null,
    targetHighlightId: null as string | null,
    currentPassageIndex: 0,
  });
  const [reviewData, setReviewData] = useState<QuantityReviewResult | null>(null);
  const [claimDocuments, setClaimDocuments] = useState<DocumentResponse[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);

  // Fetch claim documents and existing review on component mount
  React.useEffect(() => {
    fetchClaimDocuments();
    fetchExistingReview();
    
    // Cleanup handled by useBackgroundJobStatus hook
  }, []);

  const fetchClaimDocuments = async () => {
    try {
      setDocumentsLoading(true);
      console.log('fetchClaimDocuments: Starting document fetch');
      
      const claimDocsResponse = await apiRequest("GET", `/api/claims/${claim.claimId}/documents`);
      const claimDocsData: DocumentResponse[] = await claimDocsResponse.json();
      console.log('fetchClaimDocuments: Claim docs response:', claimDocsData);
      setClaimDocuments(claimDocsData || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const fetchExistingReview = async () => {
    try {
      const response = await apiRequest("GET", `/api/claims/${claim.claimId}/quantity-review`);
      const reviewResult: QuantityReviewResult = await response.json();
      if (reviewResult) {
        setReviewData(reviewResult);
        setReviewState(prev => ({ 
          ...prev, 
          status: reviewResult.status,
          lastReviewDate: reviewResult.completedAt ? new Date(reviewResult.completedAt) : null,
        }));
        
        // If processing, start polling
        if (reviewResult.status === 'processing') {
          // startPolling(); // TODO: Implement
        }
      }
    } catch {
      // No existing review - keep status as not_started
      console.log('No existing review found');
    }
  };


  const handleTriggerReview = async () => {
    if (claimDocuments.length === 0) {
      toast({
        title: "No Claim Documents",
        description: "Claim documents must be uploaded before conducting a quantity review.",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await apiRequest("POST", `/api/claims/${claim.claimId}/quantity-review`);
      const _result = await response.json();
      
      // Only set processing state after successful API call
      setReviewState(prev => ({ ...prev, status: 'processing', processingStartTime: new Date() }));
      
      toast({
        title: "Review Started",
        description: "The automated quantity review has been started. Progress will be tracked automatically.",
      });
      
      // Background job tracking will handle completion automatically
    } catch (error: unknown) {
      // Check for 409 conflict error specifically
      if (error instanceof Error && (error.message.includes('409') || error.message.includes('already in progress'))) {
        toast({
          title: "Review Already Running",
          description: "A quantity review is already in progress. Please wait for it to complete before starting a new one.",
          variant: "destructive",
        });
      } else {
        const errorMessage = error instanceof Error ? error.message : "Failed to start quantity review.";
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      }
    }
  };

  const handleItemExpansion = (itemId: string) => {
    const wasExpanded = reviewState.expandedItemId === itemId;
    const newExpandedId = wasExpanded ? null : itemId;
    
    setReviewState(prev => ({
      ...prev,
      expandedItemId: newExpandedId,
      targetHighlightId: newExpandedId,
      currentPassageIndex: 0, // Reset to first passage when expanding new item
    }));

    // Auto-open appropriate document when expanding review item
    if (newExpandedId && reviewData?.reviewData?.review_items) {
      const expandedItem = reviewData.reviewData.review_items.find(item => item.item_id === itemId);
      if (expandedItem?.claim_passage?.document_id) {
        setReviewState(prev => ({
          ...prev,
          activeDocumentId: expandedItem.claim_passage?.document_id || null,
        }));
      }
    }
  };

  const handlePassageNavigation = (direction: 'prev' | 'next', maxPassages: number) => {
    const newIndex = direction === 'next' 
      ? Math.min(reviewState.currentPassageIndex + 1, maxPassages - 1)
      : Math.max(reviewState.currentPassageIndex - 1, 0);
    
    setReviewState(prev => ({
      ...prev,
      currentPassageIndex: newIndex,
    }));
  };

  const handleDocumentSelect = (documentId: string) => {
    setReviewState(prev => ({ ...prev, activeDocumentId: documentId }));
  };

  const getJudgmentIcon = (judgment: 'valid' | 'excessive' | 'unclear') => {
    switch (judgment) {
      case 'valid':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'excessive':
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      case 'unclear':
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      default:
        return null;
    }
  };

  const getJudgmentColor = (judgment: 'valid' | 'excessive' | 'unclear') => {
    switch (judgment) {
      case 'valid':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'excessive':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'unclear':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return '';
    }
  };

  const getJudgmentRing = (judgment: 'valid' | 'excessive' | 'unclear') => {
    switch (judgment) {
      case 'valid':
        return 'ring-2 ring-green-300/70';
      case 'excessive':
        return 'ring-2 ring-red-300/70';
      case 'unclear':
        return 'ring-2 ring-yellow-300/70';
      default:
        return '';
    }
  };

  const handleAdvanceStage = async () => {
    try {
      await apiRequest("PUT", `/api/claims/${claim.claimId}`, { 
        status: "Calculation Review" 
      });
      
      queryClient.invalidateQueries({ queryKey: [`/api/claims/${claim.claimId}`] });
      
      toast({
        title: "Stage completed",
        description: "Moved to next stage: Calculation Review",
      });
      
      onClaimUpdate();
    } catch {
      toast({
        title: "Error",
        description: "Failed to advance to next stage.",
        variant: "destructive",
      });
    }
  };

  // const handleDownload = async (documentUrl: string, documentName: string) => {
  //   try {
  //     // First fetch the download URL from the API with proper authentication
  //     const response = await apiRequest("GET", documentUrl);
  //     const data = await response.json();
  //     
  //     // Open the signed URL in a new tab
  //     window.open(data.downloadUrl, '_blank');
  //     
  //     toast({
  //       title: "Download started",
  //       description: `Downloading ${documentName}`,
  //     });
  //   } catch (error) {
  //     console.error('Download error:', error);
  //     toast({
  //       title: "Download failed",
  //       description: error instanceof Error ? error.message : "Failed to download document",
  //       variant: "destructive",
  //     });
  //   }
  // };

  interface TextHighlight {
    text: string;
    pageNumber: number;
    reviewItemId?: string;
    reviewItemIndex?: number;
    judgment?: 'valid' | 'excessive' | 'unclear';
    reviewItemTitle?: string;
  }

  interface ReviewItem {
    item_id: string;
    title: string;
    judgment: 'valid' | 'excessive' | 'unclear';
    claim_passage?: {
      text: string;
      document_id?: string;
      page_number?: number;
    };
  }

  interface ReviewData {
    reviewData?: {
      review_items?: ReviewItem[];
    };
  }

  const createTextHighlights = (
    reviewData: ReviewData | undefined,
    activeReviewItemId?: string | null
  ): TextHighlight[] => {
    const textHighlights: TextHighlight[] = [];
    
    if (!reviewData?.reviewData?.review_items || !activeReviewItemId) {
      return textHighlights;
    }

    const activeItem = reviewData.reviewData.review_items.find((item: ReviewItem) => 
      item.item_id === activeReviewItemId
    );

    if (!activeItem) {
      return textHighlights;
    }

    const itemIndex = reviewData.reviewData.review_items.findIndex((item: ReviewItem) => 
      item.item_id === activeReviewItemId
    );

    if (activeItem.claim_passage?.text) {
      textHighlights.push({
        text: activeItem.claim_passage.text,
        pageNumber: activeItem.claim_passage.page_number || 1,
        reviewItemId: activeItem.item_id,
        reviewItemIndex: itemIndex + 1,
        judgment: activeItem.judgment,
        reviewItemTitle: activeItem.title,
      });
    }

    return textHighlights;
  };

  const createDocumentDataWithHighlights = (documents: DocumentResponse[]) => {
    return documents.map(doc => {
      let targetPage = 1;

      const textHighlights = createTextHighlights(reviewData as ReviewData | undefined, reviewState.expandedItemId);
      
      const documentTextHighlights = textHighlights.filter(h => 
        reviewData?.reviewData?.review_items?.some((item: ReviewItem) => 
          item.claim_passage?.document_id === doc.documentMetadataId && item.claim_passage?.text === h.text
        )
      );

      if (documentTextHighlights.length > 0) {
        targetPage = documentTextHighlights[0].pageNumber;
      }

      return {
        documentId: doc.documentMetadataId,
        documentName: doc.fileName,
        documentUrl: `/api/documents/${doc.documentMetadataId}/download`,
        textHighlights: documentTextHighlights,
        targetPage
      };
    });
  };

  // Check for active quantity review jobs
  const activeQuantityReviewJob = activeJobs.find(job => job.job_type === 'quantity_review') || null;
  const hasActiveQuantityReviewJob = activeQuantityReviewJob || reviewState.status === 'processing';
  
  // If quantity review is processing, show AsyncProgress
  if (hasActiveQuantityReviewJob) {
    const checkpoints = createQuantityReviewCheckpoints(claimDocuments.length);
    
    // Get current checkpoint from background job if available
    const currentCheckpointId = (activeQuantityReviewJob?.processing_data && typeof activeQuantityReviewJob.processing_data === 'object' && 'current_checkpoint' in activeQuantityReviewJob.processing_data && typeof activeQuantityReviewJob.processing_data.current_checkpoint === 'string') ? activeQuantityReviewJob.processing_data.current_checkpoint : 'initializing';
    const jobStartTime = activeQuantityReviewJob?.started_at ? new Date(activeQuantityReviewJob.started_at) : reviewState.processingStartTime;
    
    // Map background job status to AsyncProgress status
    const progressStatus = activeQuantityReviewJob ? activeQuantityReviewJob.status as 'pending' | 'processing' | 'completed' | 'failed' | 'retry' : 'processing';
    
    // Create documents array for display
    const allDocuments = claimDocuments.map(doc => ({ fileName: doc.fileName, documentType: 'Claim Document' }));
    
    return (
      <div className="space-y-6">
        <AsyncProgress
          status={progressStatus}
          workflowTitle="Quantity Review Analysis"
          workflowDescription="AI agents are analyzing claimed quantities and validating them against project records and industry standards."
          checkpoints={checkpoints}
          currentCheckpointId={currentCheckpointId}
          startTime={jobStartTime || undefined}
          documents={allDocuments}
          onCancel={() => {
            setReviewState(prev => ({ ...prev, status: 'not_started', processingStartTime: null }));
            toast({
              title: "Review cancelled",
              description: "You can restart the quantity review from this stage.",
            });
          }}
          showAIIndicator={true}
        />
      </div>
    );
  }

  // const reviewItems: unknown[] = []; // TODO: Implement proper review items fetching
  const documentsWithHighlights = createDocumentDataWithHighlights(claimDocuments);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <RulerIcon className="h-5 w-5 mr-2 text-primary" />
            Quantity Review
          </CardTitle>
          <CardDescription>
            Analyze claimed quantities and rates against project records and industry standards.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Review Trigger Section */}
          <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
            <div className="space-y-1">
              <h4 className="font-medium">Automated Quantity Review</h4>
              <p className="text-sm text-muted-foreground">
                {reviewState.status === 'not_started' && "Ready to analyze claimed quantities and validate against project records"}
                {reviewState.status === 'completed' && `Review completed ${reviewState.lastReviewDate ? formatDate(reviewState.lastReviewDate.toISOString()) : ''}`}
                {reviewState.status === 'failed' && "Review failed - please try again"}
              </p>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span>📄 Claim docs: {claimDocuments.length}</span>
              </div>
            </div>
            <div className="flex gap-2">
              {(reviewState.status === 'not_started' || reviewState.status === 'failed') && (
                <Button 
                  onClick={handleTriggerReview}
                  disabled={claimDocuments.length === 0}
                  className="min-w-[160px]"
                >
                  <ZapIcon className="h-4 w-4 mr-2" />
                  Conduct Quantity Review
                </Button>
              )}
              {reviewState.status === 'completed' && (
                <Button variant="outline" onClick={handleTriggerReview} className="min-w-[160px]">
                  <ZapIcon className="h-4 w-4 mr-2" />
                  Re-Run Quantity Review
                </Button>
              )}
            </div>
          </div>

          {/* Loading indicator */}
          {documentsLoading && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span className="ml-2 text-sm text-muted-foreground">Loading documents...</span>
            </div>
          )}

          {/* Warning for missing documents - only show when not loading */}
          {!documentsLoading && claimDocuments.length === 0 && (
            <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Missing Documents</AlertTitle>
            <AlertDescription>
                No claim documents uploaded. Please ensure claim documents are uploaded before conducting the review.
            </AlertDescription>
          </Alert>
          )}

          {/* Review Results Display */}
          {reviewState.status === 'completed' && reviewData?.reviewData && (
            <div className="space-y-6">
              {/* Summary Section */}
              <div className="prose prose-sm max-w-none">
                <div className="text-sm bg-slate-50 p-4 rounded-lg border">
                  <ReactMarkdown>{reviewData?.reviewData?.summary || 'No summary available'}</ReactMarkdown>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Review Items List */}
                <div className="space-y-4">
                  {reviewData.reviewData.review_items && reviewData.reviewData.review_items.length > 0 ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">Review Items ({reviewData.reviewData.review_items.length})</h4>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowAddItemModal(true)}
                          disabled={isCreating}
                        >
                          <MessageSquare className="h-3 w-3 mr-1" />
                          Add Item
                        </Button>
                      </div>
                      {reviewData.reviewData.review_items.map((item, index: number) => (
                        <div 
                          key={item.item_id || index}
                          className={`border rounded-lg transition-all duration-200 ${getJudgmentColor(item.judgment)} ${
                            reviewState.expandedItemId === item.item_id ? getJudgmentRing(item.judgment) : ''
                          }`}
                        >
                          <div 
                            className="p-4 cursor-pointer hover:bg-black/5 transition-colors"
                            onClick={() => handleItemExpansion(item.item_id)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="font-mono text-sm text-muted-foreground">
                                  {String(index + 1).padStart(2, '0')}
                                </span>
                                {getJudgmentIcon(item.judgment)}
                                <span className="font-medium">{item.title}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <EditableJudgmentBadge
                                  value={item.judgment}
                                  reviewType="quantity"
                                  claimId={claim.claimId}
                                  itemId={item.item_id}
                                  isAiGenerated={item.is_ai_generated}
                                  isModified={item.is_modified}
                                  lastModifiedBy={item.last_modified_by}
                                  isEditing={isEditing(item.item_id)}
                                  onEdit={(judgment) => editItem({
                                    claimId: claim.claimId,
                                    reviewType: 'quantity',
                                    itemId: item.item_id,
                                    judgment
                                  })}
                                />
                                {item.is_ai_generated === false && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (confirm('Are you sure you want to delete this review item?')) {
                                        deleteItem({
                                          claimId: claim.claimId,
                                          reviewType: 'quantity',
                                          itemId: item.item_id,
                                        });
                                      }
                                    }}
                                    disabled={isDeleting}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    title="Delete review item"
                                  >
                                    <BanIcon className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {reviewState.expandedItemId === item.item_id && (
                            <div className="border-t bg-white/50 p-4 space-y-4">
                              <div>
                                <p className="text-muted-foreground mb-3 text-sm">Analysis:</p>
                                <EditableAnalysis
                                  value={item.explanation}
                                  originalValue={item.original_explanation}
                                  reviewType="quantity"
                                  claimId={claim.claimId}
                                  itemId={item.item_id}
                                  isAiGenerated={item.is_ai_generated}
                                  isModified={item.is_modified}
                                  lastModifiedBy={item.last_modified_by}
                                  lastModifiedAt={item.last_modified_at}
                                  editHistory={item.edit_history}
                                  isEditing={isEditing(item.item_id)}
                                  onEdit={(explanation) => editExplanation(
                                    claim.claimId,
                                    'quantity',
                                    item.item_id,
                                    explanation
                                  )}
                                />
                              </div>
                              
                              {/* Claim Passage */}
                              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                                <p className="text-xs font-medium text-blue-700 mb-1">
                                  Claim Document - Page {item.claim_passage?.page_number || 1}
                                </p>
                                <p className="text-sm">{item.claim_passage?.text}</p>
                              </div>
                              
                              {/* Contract Passages */}
                              {item.contract_passages && item.contract_passages.length > 0 && (
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <p className="text-xs font-medium text-green-700">
                                      Contract References ({item.contract_passages.length})
                                    </p>
                                    {item.contract_passages.length > 1 && (
                                      <div className="flex items-center gap-2 text-xs">
                                        <Button 
                                          variant="ghost" 
                                          size="sm"
                                          onClick={() => handlePassageNavigation('prev', item.contract_passages?.length || 0)}
                                          disabled={reviewState.currentPassageIndex === 0}
                                        >
                                          <ChevronLeft className="h-3 w-3" />
                                        </Button>
                                        <span className="text-muted-foreground">
                                          {reviewState.currentPassageIndex + 1} of {item.contract_passages.length}
                                        </span>
                                        <Button 
                                          variant="ghost" 
                                          size="sm"
                                          onClick={() => handlePassageNavigation('next', item.contract_passages?.length || 0)}
                                          disabled={reviewState.currentPassageIndex === (item.contract_passages?.length || 1) - 1}
                                        >
                                          <ChevronRight className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                  
                                  <div className="bg-green-50 p-3 rounded border border-green-200">
                                    <p className="text-xs font-medium text-green-700 mb-1">
                                      Contract Document - Page {item.contract_passages?.[reviewState.currentPassageIndex]?.page_number || 1}
                                    </p>
                                    <p className="text-sm">
                                      {item.contract_passages?.[reviewState.currentPassageIndex]?.text || 'No contract reference available'}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <h4 className="font-medium">Review Items (0)</h4>
                      <p className="text-sm text-muted-foreground">No review items found.</p>
                    </div>
                  )}
                </div>

                {/* Document Preview Column */}
                <div className="space-y-4">
                  <h4 className="font-medium">Document Review</h4>
                  {documentsWithHighlights.length > 0 ? (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center justify-between">
                          <span className="flex items-center">
                            <FileText className="h-5 w-5 mr-2 text-blue-600" />
                            Claim Documents ({documentsWithHighlights.length})
                          </span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Accordion 
                          type="single" 
                          collapsible 
                          value={reviewState.activeDocumentId || ""}
                          onValueChange={handleDocumentSelect}
                        >
                          {documentsWithHighlights.map((doc) => (
                            <AccordionItem key={doc.documentId} value={doc.documentId}>
                              <div className="flex items-center">
                                <AccordionTrigger className="flex-1 hover:no-underline">
                                  <div className="flex items-center w-full">
                                    <FileText className="h-4 w-4 text-red-500" />
                                    <span className="ml-2 text-sm font-medium truncate max-w-[200px]" title={doc.documentName}>
                                      {doc.documentName}
                                    </span>
                                    {doc.textHighlights && doc.textHighlights.length > 0 && (
                                      <Badge variant="secondary" className="ml-2 text-xs bg-yellow-100 text-yellow-700">
                                        📍 {doc.textHighlights.length}
                                      </Badge>
                                    )}
                                  </div>
                                </AccordionTrigger>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="ml-2 shrink-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // TODO: Implement handleDownload
                                    console.log('Download requested for:', doc.documentName);
                                  }}
                                >
                                  <Download className="h-3 w-3" />
                                </Button>
                              </div>
                              <AccordionContent>
                                <ReactPDFViewer
                                  documentUrl={doc.documentUrl}
                                  documentName={doc.documentName}
                                  textHighlights={doc.textHighlights || []}
                                  activePageNumber={doc.targetPage || 1}
                                  autoScrollToHighlight={doc.documentId === reviewState.activeDocumentId}
                                  targetHighlightId={reviewState.targetHighlightId || undefined}
                                  maxHeight="500px"
                                  onHighlightClick={(highlight) => console.log('Highlight clicked:', highlight)}
                                />
                              </AccordionContent>
                            </AccordionItem>
                          ))}
                        </Accordion>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="text-center py-8 border rounded-lg">
                      <p className="text-muted-foreground">No documents available for review</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Processing Notes */}
              {reviewData.extractionNotes && (
                <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded">
                  <strong>Processing Notes:</strong> {reviewData.extractionNotes}
                </div>
              )}
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button variant="outline" onClick={onClaimUpdate}>
            Refresh Review Status
          </Button>
          {reviewState.status === 'completed' && (
            <Button onClick={handleAdvanceStage}>
              Proceed to Calculation Review
              <CheckCircle className="h-4 w-4 ml-2" />
            </Button>
          )}
        </CardFooter>
      </Card>
      
      {/* Add Review Item Modal */}
      <AddReviewItemModal
        isOpen={showAddItemModal}
        onOpenChange={setShowAddItemModal}
        reviewType="quantity"
        claimId={claim.claimId}
        onSubmit={(data) => {
          createItem({
            claimId: claim.claimId,
            reviewType: 'quantity',
            title: data.title,
            judgment: data.judgment,
            explanation: data.explanation,
            claim_passage: data.claim_passage,
            contract_passages: data.contract_passages,
          });
          setShowAddItemModal(false);
        }}
        isLoading={isCreating}
      />
      
      {/* Contract Add Review Item Modal */}
      <AddReviewItemModal
        isOpen={showContractAddItemModal}
        onOpenChange={setShowContractAddItemModal}
        reviewType="contract"
        claimId={claim.claimId}
        onSubmit={(data) => {
          createItem({
            claimId: claim.claimId,
            reviewType: 'contract',
            title: data.title,
            judgment: data.judgment,
            explanation: data.explanation,
            claim_passage: data.claim_passage,
            contract_passages: data.contract_passages,
          });
          setShowContractAddItemModal(false);
        }}
        isLoading={isCreating}
      />
    </div>
  );
}

// Calculation Review Flow
function ReviewCalculationsFlow({ 
  claim, 
  isReviewMode: _isReviewMode = false, 
  onClaimUpdate, 
  onReturnToActiveStage: _onReturnToActiveStage 
}: { 
  claim: Claim, 
  isReviewMode?: boolean, 
  onClaimUpdate: () => void, 
  onReturnToActiveStage?: () => void 
}) {
  const { toast } = useToast();
  
  // Review editing functionality
  const { editItem, createItem, deleteItem, isEditing, isCreating, isDeleting } = useReviewEdit();
  
  // Modal state for adding custom review items
  const [showCalculationAddItemModal, setShowCalculationAddItemModal] = useState(false);
  
  // Background job tracking for calculation review
  const { jobs: _jobs, activeJobs, overallStatus: _overallStatus } = useBackgroundJobStatus({
    entityId: claim.claimId,
    entityType: 'claim',
    onComplete: (job) => {
      console.log('Calculation review job completed:', job);
      if (job.job_type === 'calculation_review') {
        setReviewState(prev => ({ ...prev, status: 'completed' }));
        fetchExistingReview(); // Refresh review data
        onClaimUpdate();
        
        toast({
          title: "Calculation review completed",
          description: "The calculation review has been completed successfully."
        });
      }
    },
    onFailed: (job) => {
      console.log('Calculation review job failed:', job);
      if (job.job_type === 'calculation_review') {
        setReviewState(prev => ({ ...prev, status: 'failed' }));
        
        toast({
          title: "Calculation review failed",
          description: job.error_message || "Review processing failed. Please try again.",
          variant: "destructive"
        });
      }
    }
  });
  
  interface DocumentResponse {
    documentMetadataId: string;
    fileName: string;
    documentType?: string;
    fileSize?: number;
    uploadDate?: string;
    createdAt?: string;
    currentVersionId?: string;
    filePath?: string;
  }
  
  interface CalculationReviewResult {
    status: 'not_started' | 'processing' | 'completed' | 'failed';
    completedAt?: string;
    reviewData?: {
      summary?: string;
      review_items?: Array<{
        item_id: string;
        title: string;
        judgment: 'valid' | 'invalid' | 'unclear';
        explanation: string;
        claim_passage?: {
          text: string;
          page_number?: number;
          document_id?: string;
        };
        // Audit trail fields
        original_judgment?: string;
        original_explanation?: string;
        is_ai_generated?: boolean;
        is_modified?: boolean;
        created_by?: string;
        last_modified_by?: string;
        last_modified_at?: string;
        is_deleted?: boolean;
        edit_history?: Array<{
          editId: string;
          action: 'created' | 'modified_judgment' | 'modified_explanation' | 'deleted' | 'restored';
          userId: string;
          userName: string;
          timestamp: string;
          previousValues?: { judgment?: string; explanation?: string; };
          newValues?: { judgment?: string; explanation?: string; };
        }>;
      }>;
    };
    extractionNotes?: string;
  }
  
  // Removed unused CalculationReviewStatus interface
  
  const [reviewState, setReviewState] = useState({
    status: 'not_started' as 'not_started' | 'processing' | 'completed' | 'failed',
    lastReviewDate: null as Date | null,
    processingStartTime: null as Date | null,
    expandedItemId: null as string | null,
    activeDocumentId: null as string | null,
    targetHighlightId: null as string | null,
  });
  const [reviewData, setReviewData] = useState<CalculationReviewResult | null>(null);
  const [claimDocuments, setClaimDocuments] = useState<DocumentResponse[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);

  // Fetch claim documents and existing review on component mount
  React.useEffect(() => {
    fetchClaimDocuments();
    fetchExistingReview();
    
    // Cleanup handled by useBackgroundJobStatus hook
  }, []);

  const fetchClaimDocuments = async () => {
    try {
      setDocumentsLoading(true);
      console.log('fetchClaimDocuments: Starting document fetch');
      
      const claimDocsResponse = await apiRequest("GET", `/api/claims/${claim.claimId}/documents`);
      const claimDocsData: DocumentResponse[] = await claimDocsResponse.json();
      console.log('fetchClaimDocuments: Claim docs response:', claimDocsData);
      setClaimDocuments(claimDocsData || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const fetchExistingReview = async () => {
    try {
      const response = await apiRequest("GET", `/api/claims/${claim.claimId}/calculation-review`);
      const reviewResult: CalculationReviewResult = await response.json();
      if (reviewResult) {
        setReviewData(reviewResult);
        setReviewState(prev => ({ 
          ...prev, 
          status: reviewResult.status,
          lastReviewDate: reviewResult.completedAt ? new Date(reviewResult.completedAt) : null,
        }));
        
        // If processing, start polling
        if (reviewResult.status === 'processing') {
          // startPolling(); // TODO: Implement
        }
      }
    } catch {
      // No existing review - keep status as not_started
      console.log('No existing review found');
    }
  };


  const handleTriggerReview = async () => {
    if (claimDocuments.length === 0) {
      toast({
        title: "No Claim Documents",
        description: "Claim documents must be uploaded before conducting a calculation review.",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await apiRequest("POST", `/api/claims/${claim.claimId}/calculation-review`);
      const _result = await response.json();
      
      // Only set processing state after successful API call
      setReviewState(prev => ({ ...prev, status: 'processing', processingStartTime: new Date() }));
      
      toast({
        title: "Review Started",
        description: "The automated calculation review has been started. Progress will be tracked automatically.",
      });
      
      // Background job tracking will handle completion automatically
    } catch (error: unknown) {
      // Check for 409 conflict error specifically
      if (error instanceof Error && (error.message.includes('409') || error.message.includes('already in progress'))) {
        toast({
          title: "Review Already Running",
          description: "A calculation review is already in progress. Please wait for it to complete before starting a new one.",
          variant: "destructive",
        });
      } else {
        const errorMessage = error instanceof Error ? error.message : "Failed to start calculation review.";
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      }
    }
  };

  const handleItemExpansion = (itemId: string) => {
    const wasExpanded = reviewState.expandedItemId === itemId;
    const newExpandedId = wasExpanded ? null : itemId;
    
    setReviewState(prev => ({
      ...prev,
      expandedItemId: newExpandedId,
      targetHighlightId: newExpandedId,
    }));

    // Auto-open appropriate document when expanding review item
    if (newExpandedId && reviewData?.reviewData?.review_items) {
      const expandedItem = reviewData.reviewData.review_items.find(item => item.item_id === itemId);
      if (expandedItem?.claim_passage?.document_id) {
        setReviewState(prev => ({
          ...prev,
          activeDocumentId: expandedItem.claim_passage?.document_id || null,
        }));
      }
    }
  };

  const handleDocumentSelect = (documentId: string) => {
    setReviewState(prev => ({ ...prev, activeDocumentId: documentId }));
  };

  const getJudgmentIcon = (judgment: 'valid' | 'invalid' | 'unclear') => {
    switch (judgment) {
      case 'valid':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'invalid':
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      case 'unclear':
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      default:
        return null;
    }
  };

  const getJudgmentColor = (judgment: 'valid' | 'invalid' | 'unclear') => {
    switch (judgment) {
      case 'valid':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'invalid':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'unclear':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return '';
    }
  };

  const getJudgmentRing = (judgment: 'valid' | 'invalid' | 'unclear') => {
    switch (judgment) {
      case 'valid':
        return 'ring-2 ring-green-300/70';
      case 'invalid':
        return 'ring-2 ring-red-300/70';
      case 'unclear':
        return 'ring-2 ring-yellow-300/70';
      default:
        return '';
    }
  };

  const handleAdvanceStage = async () => {
    try {
      await apiRequest("PUT", `/api/claims/${claim.claimId}`, { 
        status: "Final Decision" 
      });
      
      queryClient.invalidateQueries({ queryKey: [`/api/claims/${claim.claimId}`] });
      
      toast({
        title: "Stage completed",
        description: "Moved to next stage: Final Decision",
      });
      
      onClaimUpdate();
    } catch {
      toast({
        title: "Error",
        description: "Failed to advance to next stage.",
        variant: "destructive",
      });
    }
  };

  // const handleDownload = async (documentUrl: string, documentName: string) => {
  //   try {
  //     // First fetch the download URL from the API with proper authentication
  //     const response = await apiRequest("GET", documentUrl);
  //     const data = await response.json();
  //     
  //     // Open the signed URL in a new tab
  //     window.open(data.downloadUrl, '_blank');
  //     
  //     toast({
  //       title: "Download started",
  //       description: `Downloading ${documentName}`,
  //     });
  //   } catch (error) {
  //     console.error('Download error:', error);
  //     toast({
  //       title: "Download failed",
  //       description: error instanceof Error ? error.message : "Failed to download document",
  //       variant: "destructive",
  //     });
  //   }
  // };

  interface TextHighlight {
    text: string;
    pageNumber: number;
    reviewItemId?: string;
    reviewItemIndex?: number;
    judgment?: 'valid' | 'invalid' | 'unclear';
    reviewItemTitle?: string;
  }

  interface ReviewItem {
    item_id: string;
    title: string;
    judgment: 'valid' | 'invalid' | 'unclear';
    claim_passage?: {
      text: string;
      document_id?: string;
      page_number?: number;
    };
  }

  interface ReviewData {
    reviewData?: {
      review_items?: ReviewItem[];
    };
  }

  const createTextHighlights = (
    reviewData: ReviewData | undefined,
    activeReviewItemId?: string | null
  ): TextHighlight[] => {
    const textHighlights: TextHighlight[] = [];
    
    if (!reviewData?.reviewData?.review_items || !activeReviewItemId) {
      return textHighlights;
    }

    const activeItem = reviewData.reviewData.review_items.find((item: ReviewItem) => 
      item.item_id === activeReviewItemId
    );

    if (!activeItem) {
      return textHighlights;
    }

    const itemIndex = reviewData.reviewData.review_items.findIndex((item: ReviewItem) => 
      item.item_id === activeReviewItemId
    );

    if (activeItem.claim_passage?.text) {
      textHighlights.push({
        text: activeItem.claim_passage.text,
        pageNumber: activeItem.claim_passage.page_number || 1,
        reviewItemId: activeItem.item_id,
        reviewItemIndex: itemIndex + 1,
        judgment: activeItem.judgment,
        reviewItemTitle: activeItem.title,
      });
    }

    return textHighlights;
  };

  const createDocumentDataWithHighlights = (documents: DocumentResponse[]) => {
    return documents.map(doc => {
      let targetPage = 1;

      const textHighlights = createTextHighlights(reviewData as ReviewData | undefined, reviewState.expandedItemId);
      
      const documentTextHighlights = textHighlights.filter(h => 
        reviewData?.reviewData?.review_items?.some((item: ReviewItem) => 
          item.claim_passage?.document_id === doc.documentMetadataId && item.claim_passage?.text === h.text
        )
      );

      if (documentTextHighlights.length > 0) {
        targetPage = documentTextHighlights[0].pageNumber;
      }

      return {
        documentId: doc.documentMetadataId,
        documentName: doc.fileName,
        documentUrl: `/api/documents/${doc.documentMetadataId}/download`,
        textHighlights: documentTextHighlights,
        targetPage
      };
    });
  };

  // Check for active calculation review jobs
  const activeCalculationReviewJob = activeJobs.find(job => job.job_type === 'calculation_review') || null;
  const hasActiveCalculationReviewJob = activeCalculationReviewJob || reviewState.status === 'processing';
  
  // If calculation review is processing, show AsyncProgress
  if (hasActiveCalculationReviewJob) {
    const checkpoints = createCalculationReviewCheckpoints(claimDocuments.length);
    
    // Get current checkpoint from background job if available
    const currentCheckpointId = (activeCalculationReviewJob?.processing_data && typeof activeCalculationReviewJob.processing_data === 'object' && 'current_checkpoint' in activeCalculationReviewJob.processing_data && typeof activeCalculationReviewJob.processing_data.current_checkpoint === 'string') ? activeCalculationReviewJob.processing_data.current_checkpoint : 'initializing';
    const jobStartTime = activeCalculationReviewJob?.started_at ? new Date(activeCalculationReviewJob.started_at) : reviewState.processingStartTime;
    
    // Map background job status to AsyncProgress status
    const progressStatus = activeCalculationReviewJob ? activeCalculationReviewJob.status as 'pending' | 'processing' | 'completed' | 'failed' | 'retry' : 'processing';
    
    // Create documents array for display
    const allDocuments = claimDocuments.map(doc => ({ fileName: doc.fileName, documentType: 'Claim Document' }));
    
    return (
      <div className="space-y-6">
        <AsyncProgress
          status={progressStatus}
          workflowTitle="Calculation Review Analysis"
          workflowDescription="AI agents are verifying the mathematical accuracy of calculations, markups, and cost summations in the claim documents."
          checkpoints={checkpoints}
          currentCheckpointId={currentCheckpointId}
          startTime={jobStartTime || undefined}
          documents={allDocuments}
          onCancel={() => {
            setReviewState(prev => ({ ...prev, status: 'not_started', processingStartTime: null }));
            toast({
              title: "Review cancelled",
              description: "You can restart the calculation review from this stage.",
            });
          }}
          showAIIndicator={true}
        />
      </div>
    );
  }

  // const reviewItems: unknown[] = []; // TODO: Implement proper review items fetching
  const documentsWithHighlights = createDocumentDataWithHighlights(claimDocuments);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Calculator className="h-5 w-5 mr-2 text-primary" />
            Calculation Review
          </CardTitle>
          <CardDescription>
            Verify the mathematical accuracy of calculations, markups, and cost summations in the claim.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Review Trigger Section */}
          <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
            <div className="space-y-1">
              <h4 className="font-medium">Automated Calculation Review</h4>
              <p className="text-sm text-muted-foreground">
                {reviewState.status === 'not_started' && "Ready to analyze claimed calculations and verify mathematical accuracy"}
                {reviewState.status === 'completed' && `Review completed ${reviewState.lastReviewDate ? formatDate(reviewState.lastReviewDate.toISOString()) : ''}`}
                {reviewState.status === 'failed' && "Review failed - please try again"}
              </p>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span>📄 Claim docs: {claimDocuments.length}</span>
              </div>
            </div>
            <div className="flex gap-2">
              {(reviewState.status === 'not_started' || reviewState.status === 'failed') && (
                <Button 
                  onClick={handleTriggerReview}
                  disabled={claimDocuments.length === 0}
                  className="min-w-[160px]"
                >
                  <ZapIcon className="h-4 w-4 mr-2" />
                  Conduct Calculation Review
                </Button>
              )}
              {reviewState.status === 'completed' && (
                <Button variant="outline" onClick={handleTriggerReview} className="min-w-[160px]">
                  <ZapIcon className="h-4 w-4 mr-2" />
                  Re-Run Calculation Review
                </Button>
              )}
            </div>
          </div>

          {/* Loading indicator */}
          {documentsLoading && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span className="ml-2 text-sm text-muted-foreground">Loading documents...</span>
            </div>
          )}

          {/* Warning for missing documents - only show when not loading */}
          {!documentsLoading && claimDocuments.length === 0 && (
            <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Missing Documents</AlertTitle>
            <AlertDescription>
                No claim documents uploaded. Please ensure claim documents are uploaded before conducting the review.
            </AlertDescription>
          </Alert>
          )}

          {/* Review Results Display */}
          {reviewState.status === 'completed' && reviewData?.reviewData && (
            <div className="space-y-6">
              {/* Summary Section */}
              <div className="prose prose-sm max-w-none">
                <div className="text-sm bg-slate-50 p-4 rounded-lg border">
                  <ReactMarkdown>{reviewData?.reviewData?.summary || 'No summary available'}</ReactMarkdown>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Review Items List */}
                <div className="space-y-4">
                  {reviewData.reviewData.review_items && reviewData.reviewData.review_items.length > 0 ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">Review Items ({reviewData.reviewData.review_items.length})</h4>
                        <Button
                          size="sm"
                          onClick={() => setShowCalculationAddItemModal(true)}
                          className="gap-2"
                        >
                          <Plus className="h-4 w-4" />
                          Add Item
                        </Button>
                      </div>
                      {reviewData.reviewData.review_items.map((item, index: number) => (
                        <div 
                          key={item.item_id || index}
                          className={`border rounded-lg transition-all duration-200 ${getJudgmentColor(item.judgment)} ${
                            reviewState.expandedItemId === item.item_id ? getJudgmentRing(item.judgment) : ''
                          }`}
                        >
                          <div 
                            className="p-4 cursor-pointer hover:bg-black/5 transition-colors"
                            onClick={() => handleItemExpansion(item.item_id)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="font-mono text-sm text-muted-foreground">
                                  {String(index + 1).padStart(2, '0')}
                                </span>
                                {getJudgmentIcon(item.judgment)}
                                <span className="font-medium">{item.title}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <EditableJudgmentBadge
                                  value={item.judgment}
                                  reviewType="calculation"
                                  claimId={claim.claimId}
                                  itemId={item.item_id}
                                  isAiGenerated={item.is_ai_generated}
                                  isModified={item.is_modified}
                                  lastModifiedBy={item.last_modified_by}
                                  isEditing={isEditing(item.item_id)}
                                  onEdit={(judgment) => editItem({
                                    claimId: claim.claimId,
                                    reviewType: 'calculation',
                                    itemId: item.item_id,
                                    judgment
                                  })}
                                />
                                {item.is_ai_generated === false && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (confirm('Are you sure you want to delete this review item?')) {
                                        deleteItem({
                                          claimId: claim.claimId,
                                          reviewType: 'calculation',
                                          itemId: item.item_id,
                                        });
                                      }
                                    }}
                                    disabled={isDeleting}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    title="Delete review item"
                                  >
                                    <BanIcon className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {reviewState.expandedItemId === item.item_id && (
                            <div className="border-t bg-white/50 p-4 space-y-4">
                              <div>
                                <p className="text-muted-foreground mb-2 text-sm">Analysis:</p>
                                <EditableAnalysis
                                  value={item.explanation}
                                  originalValue={item.original_explanation}
                                  reviewType="calculation"
                                  claimId={claim.claimId}
                                  itemId={item.item_id}
                                  isAiGenerated={item.is_ai_generated}
                                  isModified={item.is_modified}
                                  lastModifiedBy={item.last_modified_by}
                                  lastModifiedAt={item.last_modified_at}
                                  editHistory={item.edit_history}
                                  isEditing={isEditing(item.item_id)}
                                  onEdit={(explanation) => editItem({
                                    claimId: claim.claimId,
                                    reviewType: 'calculation',
                                    itemId: item.item_id,
                                    explanation
                                  })}
                                />
                              </div>
                              
                              {/* Claim Passage */}
                              {item.claim_passage && (
                                <div className="bg-blue-50 p-3 rounded border border-blue-200">
                                  <p className="text-xs font-medium text-blue-700 mb-1">
                                    Claim Document - Page {item.claim_passage?.page_number || 1}
                                  </p>
                                  <p className="text-sm">{item.claim_passage.text}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <h4 className="font-medium">Review Items (0)</h4>
                      <p className="text-sm text-muted-foreground">No review items found.</p>
                    </div>
                  )}
                </div>

                {/* Document Preview Column */}
                <div className="space-y-4">
                  <h4 className="font-medium">Document Review</h4>
                  {documentsWithHighlights.length > 0 ? (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center justify-between">
                          <span className="flex items-center">
                            <FileText className="h-5 w-5 mr-2 text-blue-600" />
                            Claim Documents ({documentsWithHighlights.length})
                          </span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Accordion 
                          type="single" 
                          collapsible 
                          value={reviewState.activeDocumentId || ""}
                          onValueChange={handleDocumentSelect}
                        >
                          {documentsWithHighlights.map((doc) => (
                            <AccordionItem key={doc.documentId} value={doc.documentId}>
                              <div className="flex items-center">
                                <AccordionTrigger className="flex-1 hover:no-underline">
                                  <div className="flex items-center w-full">
                                    <FileText className="h-4 w-4 text-red-500" />
                                    <span className="ml-2 text-sm font-medium truncate max-w-[200px]" title={doc.documentName}>
                                      {doc.documentName}
                                    </span>
                                    {doc.textHighlights && doc.textHighlights.length > 0 && (
                                      <Badge variant="secondary" className="ml-2 text-xs bg-yellow-100 text-yellow-700">
                                        📍 {doc.textHighlights.length}
                                      </Badge>
                                    )}
                                  </div>
                                </AccordionTrigger>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="ml-2 shrink-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // TODO: Implement handleDownload
                                    console.log('Download requested for:', doc.documentName);
                                  }}
                                >
                                  <Download className="h-3 w-3" />
                                </Button>
                              </div>
                              <AccordionContent>
                                <ReactPDFViewer
                                  documentUrl={doc.documentUrl}
                                  documentName={doc.documentName}
                                  textHighlights={doc.textHighlights || []}
                                  activePageNumber={doc.targetPage || 1}
                                  autoScrollToHighlight={doc.documentId === reviewState.activeDocumentId}
                                  targetHighlightId={reviewState.targetHighlightId || undefined}
                                  maxHeight="500px"
                                  onHighlightClick={(highlight) => console.log('Highlight clicked:', highlight)}
                                />
                              </AccordionContent>
                            </AccordionItem>
                          ))}
                        </Accordion>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="text-center py-8 border rounded-lg">
                      <p className="text-muted-foreground">No documents available for review</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Processing Notes */}
              {reviewData.extractionNotes && (
                <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded">
                  <strong>Processing Notes:</strong> {reviewData.extractionNotes}
                </div>
              )}
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button variant="outline" onClick={onClaimUpdate}>
            Refresh Review Status
          </Button>
          {reviewState.status === 'completed' && (
            <Button onClick={handleAdvanceStage}>
              Proceed to Final Decision
              <CheckCircle className="h-4 w-4 ml-2" />
            </Button>
          )}
        </CardFooter>
      </Card>

      {/* Calculation Add Review Item Modal */}
      <AddReviewItemModal
        isOpen={showCalculationAddItemModal}
        onOpenChange={setShowCalculationAddItemModal}
        reviewType="calculation"
        claimId={claim.claimId}
        onSubmit={(data) => {
          createItem({
            claimId: claim.claimId,
            reviewType: 'calculation',
            title: data.title,
            judgment: data.judgment,
            explanation: data.explanation,
            claim_passage: data.claim_passage,
            contract_passages: data.contract_passages,
          });
          setShowCalculationAddItemModal(false);
        }}
        isLoading={isCreating}
      />
    </div>
  );
}

// Final Decision Flow
function FinalDecisionFlow({ 
  claim, 
  isReviewMode: _isReviewMode = false, 
  onOpenEmailDialog, 
  onClaimUpdate, 
  onReturnToActiveStage: _onReturnToActiveStage 
}: { 
  claim: Claim, 
  isReviewMode?: boolean, 
  onOpenEmailDialog: () => void, 
  onClaimUpdate: () => void, 
  onReturnToActiveStage?: () => void 
}) {
  const { toast } = useToast();
  const [decision, setDecision] = useState<"approve" | "reject" | null>(null);
  const [approvedAmount, setApprovedAmount] = useState<number>(
    Math.round(claim.claimAmount * 0.85) // Default to 85% of claimed amount for demo
  );
  
  // Review data states
  const [contractReview, setContractReview] = useState<ReviewItem | null>(null);
  const [quantityReview, setQuantityReview] = useState<ReviewItem | null>(null);
  const [calculationReview, setCalculationReview] = useState<ReviewItem | null>(null);
  
  // Helper function to parse review data safely
  const parseReviewData = (review: unknown): ReviewItem | null => {
    if (!review) return null;
    
    const reviewItem = review as ReviewItem;
    
    // If reviewData is a string, parse it as JSON
    if (typeof reviewItem.reviewData === 'string') {
      try {
        reviewItem.reviewData = JSON.parse(reviewItem.reviewData);
      } catch (_error) {
        console.log('Failed to parse review data JSON');
      }
    }
    
    return reviewItem;
  };

  // Helper function to safely get review summary
  const getReviewSummary = (review: ReviewItem | null): string => {
    if (!review) return 'No notes available';
    
    // Try to get summary from reviewData (whether it's string or object)
    if (review.reviewData) {
      if (typeof review.reviewData === 'string') {
        return review.reviewData;
      } else if (typeof review.reviewData === 'object' && review.reviewData.summary) {
        return review.reviewData.summary;
      }
    }
    
    // Fall back to extractionNotes
    return review.extractionNotes || 'No notes available';
  };

  // Fetch review data on component mount
  useEffect(() => {
    const fetchReviewData = async () => {
      try {
        // Fetch contract review
        const contractResponse = await apiRequest("GET", `/api/claims/${claim.claimId}/contract-review`);
        if (contractResponse.ok) {
          const contractResult = await contractResponse.json();
          setContractReview(parseReviewData(contractResult));
        }
      } catch (_error) {
        console.log('No contract review found');
      }
      
      try {
        // Fetch quantity review
        const quantityResponse = await apiRequest("GET", `/api/claims/${claim.claimId}/quantity-review`);
        if (quantityResponse.ok) {
          const quantityResult = await quantityResponse.json();
          setQuantityReview(parseReviewData(quantityResult));
        }
      } catch (_error) {
        console.log('No quantity review found');
      }
      
      try {
        // Fetch calculation review
        const calculationResponse = await apiRequest("GET", `/api/claims/${claim.claimId}/calculation-review`);
        if (calculationResponse.ok) {
          const calculationResult = await calculationResponse.json();
          setCalculationReview(parseReviewData(calculationResult));
        }
      } catch (_error) {
        console.log('No calculation review found');
      }
    };
    
    fetchReviewData();
  }, [claim.claimId]);
  
  const handleMakeDecision = async () => {
    if (!decision) {
      toast({
        title: "Decision required",
        description: "Please select whether to approve or reject the claim.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      // Update claim status based on decision
      await apiRequest("PUT", `/api/claims/${claim.claimId}`, { 
        status: decision === "approve" ? "Approved" : "Rejected",
        approvedAmount: decision === "approve" ? approvedAmount : 0
      });
      
      queryClient.invalidateQueries({ queryKey: [`/api/claims/${claim.claimId}`] });
      
      toast({
        title: decision === "approve" ? "Claim Approved" : "Claim Rejected",
        description: `The claim has been ${decision === "approve" ? "approved" : "rejected"}.`,
      });
      
      // Open email dialog to send notification
      onOpenEmailDialog();
      onClaimUpdate();
    } catch {
      toast({
        title: "Error",
        description: "Failed to update claim status.",
        variant: "destructive",
      });
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <AlertTriangle className="h-5 w-5 mr-2 text-primary" />
          Final Decision
        </CardTitle>
        <CardDescription>
          Make a final decision on the claim based on all reviews and findings.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert variant="default" className="bg-muted/30">
          <ZapIcon className="h-4 w-4" />
          <AlertTitle>AI Recommendation Available</AlertTitle>
          <AlertDescription>
            Our system has generated a recommendation based on all reviews. Consider this in your decision-making.
          </AlertDescription>
        </Alert>
        
        {/* Decision summary */}
        <div className="space-y-4">
          <h4 className="font-medium">Claim Summary</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <BookText className="h-8 w-8 mx-auto mb-2 text-amber-500" />
                  <h3 className="font-medium">Contract Review</h3>
                  <Badge 
                    variant={contractReview?.status === 'completed' ? 'default' : 'outline'} 
                    className="mt-1"
                  >
                    {contractReview?.status === 'completed' ? 'Completed' : 'Not Reviewed'}
                  </Badge>
                  <p className="text-sm mt-2 text-muted-foreground">
                    {getReviewSummary(contractReview)}
                  </p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <RulerIcon className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                  <h3 className="font-medium">Quantity Review</h3>
                  <Badge 
                    variant={quantityReview?.status === 'completed' ? 'default' : 'outline'} 
                    className="mt-1"
                  >
                    {quantityReview?.status === 'completed' ? 'Completed' : 'Not Reviewed'}
                  </Badge>
                  <p className="text-sm mt-2 text-muted-foreground">
                    {getReviewSummary(quantityReview)}
                  </p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <Calculator className="h-8 w-8 mx-auto mb-2 text-indigo-500" />
                  <h3 className="font-medium">Calculation Review</h3>
                  <Badge 
                    variant={calculationReview?.status === 'completed' ? 'default' : 'outline'} 
                    className="mt-1"
                  >
                    {calculationReview?.status === 'completed' ? 'Completed' : 'Not Reviewed'}
                  </Badge>
                  <p className="text-sm mt-2 text-muted-foreground">
                    {getReviewSummary(calculationReview)}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* AI Recommendation */}
        <div className="space-y-4">
          <h4 className="font-medium">AI Recommendation</h4>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-start space-x-4">
                <div className="bg-primary/10 p-2 rounded-full">
                  <ZapIcon className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium text-lg">Partial Approval Recommended</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Based on all reviews, this claim should be partially approved. The contract requirements 
                    were met, but there are discrepancies in quantities and rates.
                  </p>
                  <div className="mt-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Claimed Amount:</span>
                      <span className="font-medium">{formatCurrency(claim.claimAmount, claim.claimAmountCurrency)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Recommended Amount:</span>
                      <span className="font-medium">{formatCurrency(approvedAmount, claim.claimAmountCurrency)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Adjustment:</span>
                      <span className="font-medium text-amber-600">
                        {formatCurrency(approvedAmount - claim.claimAmount, claim.claimAmountCurrency)} 
                        ({Math.round(Math.abs(((approvedAmount - claim.claimAmount) / claim.claimAmount) * 100))}%)
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Decision section */}
        <div className="space-y-4">
          <h4 className="font-medium">Make Your Decision</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className={`border-2 cursor-pointer transition ${decision === "approve" ? "border-green-500 bg-green-50" : "border-transparent"}`}
              onClick={() => setDecision("approve")}>
              <CardContent className="p-6">
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <ThumbsUp className="h-6 w-6 text-green-600 mr-3" />
                    <div>
                      <h3 className="font-medium">Approve Claim</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Approve the claim with the specified amount
                      </p>
                    </div>
                  </div>
                  <div className="w-6 h-6 rounded-full border-2 flex items-center justify-center border-muted-foreground">
                    {decision === "approve" && <div className="w-3 h-3 rounded-full bg-green-600"></div>}
                  </div>
                </div>
                
                {decision === "approve" && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label htmlFor="approvedAmount">Approved Amount</Label>
                        <div className="flex">
                          <div className="flex items-center justify-center border rounded-l px-3 bg-muted">
                            {claim.claimAmountCurrency}
                          </div>
                          <Input 
                            id="approvedAmount" 
                            type="number" 
                            className="rounded-l-none" 
                            value={approvedAmount}
                            onChange={(e) => setApprovedAmount(parseFloat(e.target.value) || 0)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            <Card className={`border-2 cursor-pointer transition ${decision === "reject" ? "border-red-500 bg-red-50" : "border-transparent"}`}
              onClick={() => setDecision("reject")}>
              <CardContent className="p-6">
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <ThumbsDown className="h-6 w-6 text-red-600 mr-3" />
                    <div>
                      <h3 className="font-medium">Reject Claim</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Reject the claim entirely
                      </p>
                    </div>
                  </div>
                  <div className="w-6 h-6 rounded-full border-2 flex items-center justify-center border-muted-foreground">
                    {decision === "reject" && <div className="w-3 h-3 rounded-full bg-red-600"></div>}
                  </div>
                </div>
                
                {decision === "reject" && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="space-y-2">
                      <Label htmlFor="rejectionReason">Reason for Rejection</Label>
                      <Textarea 
                        id="rejectionReason" 
                        rows={3} 
                        placeholder="Explain why the claim is being rejected..."
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </CardContent>
      <CardFooter className="justify-end">
        <Button variant="outline" className="mr-2">
          Request Additional Information
        </Button>
        <Button onClick={handleMakeDecision} disabled={!decision}>
          Finalize Decision
        </Button>
      </CardFooter>
    </Card>
  );
}

// Final Status Flow (for approved/rejected/withdrawn status)
function FinalStatusFlow({ claim }: { claim: Claim }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          {claim.status === "Approved" ? (
            <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
          ) : claim.status === "Rejected" ? (
            <BanIcon className="h-5 w-5 mr-2 text-red-600" />
          ) : (
            <FileQuestion className="h-5 w-5 mr-2 text-amber-600" />
          )}
          {claim.status} Claim
        </CardTitle>
        <CardDescription>
          This claim has been {claim.status.toLowerCase()} and is now in a final state.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Final status details */}
        <div className="space-y-4">
          <div className="bg-muted/30 border rounded-lg p-6">
            <div className="flex flex-col items-center justify-center text-center">
              {claim.status === "Approved" ? (
                <>
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-medium">Claim Approved</h3>
                  <p className="text-muted-foreground mt-1">
                    This claim was approved on {formatDate(claim.createdAt)}
                  </p>
                  <div className="mt-4 text-center">
                    <div className="text-sm text-muted-foreground">Approved Amount</div>
                    <div className="text-2xl font-bold mt-1">
                      {formatCurrency(claim.approvedAmount || 0, claim.claimAmountCurrency)}
                    </div>
                    <div className="text-sm text-muted-foreground mt-2">
                      Claim finalized
                    </div>
                  </div>
                </>
              ) : claim.status === "Rejected" ? (
                <>
                  <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
                    <BanIcon className="h-8 w-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-medium">Claim Rejected</h3>
                  <p className="text-muted-foreground mt-1">
                    This claim was rejected on {formatDate(claim.createdAt)}
                  </p>
                  <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 max-w-md">
                    <h4 className="font-medium text-red-800">Rejection Reasons</h4>
                    <ul className="mt-2 text-sm text-left list-disc pl-5 text-red-700">
                      <li>Contract compliance issues with notification requirements</li>
                      <li>Insufficient documentation for quantities claimed</li>
                      <li>Rates exceed contractually agreed amounts</li>
                    </ul>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-4">
                    <FileQuestion className="h-8 w-8 text-amber-600" />
                  </div>
                  <h3 className="text-xl font-medium">Claim Withdrawn</h3>
                  <p className="text-muted-foreground mt-1">
                    This claim was withdrawn on {formatDate(claim.createdAt)}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Final actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <MessageSquare className="h-5 w-5 text-primary" />
                <div>
                  <h4 className="font-medium">Communication Records</h4>
                  <p className="text-sm text-muted-foreground">
                    View all communication history related to this claim
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <FileText className="h-5 w-5 text-primary" />
                <div>
                  <h4 className="font-medium">Generate Final Report</h4>
                  <p className="text-sm text-muted-foreground">
                    Create a detailed report of the claim and decision
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </CardContent>
    </Card>
  );
}